<template>
  <div :id="component_id" class="relative" :class="['CREATE_MESSAGE','CREATE_CHANNEL'].indexOf(mode) >= 0 ? 'h-full flex flex-col' : ''">
    <!-- Tagged Users List -->
    <div class="user_tagging_suggest_container relative" :class="{'tagging-open-down': ['CREATE_MESSAGE','CREATE_CHANNEL'].indexOf(mode) >= 0}">
      <div ref="user_tagging_suggest" class="user_tagging_suggest absolute border border-border-separator rounded-md shadow-md w-full bg-surface-2" :class="show_tagged_users?'flex flex-col py-1':'invisible'">
        <a v-for="(userInfo, index) in user_tag_list" href="#" ref="user_tagging_suggest_links" @click.stop.prevent="insertContentObject('USER_TAG', userInfo)"
           class="block px-3 py-1.5 text-sm no-underline cursor-pointer hover:bg-surface-3 transition-colors" :class="active_tagged_user===index?
                       ((system_users_tagged && userInfo.personGuid.indexOf($FUSsystemUserPrefix)<0 || non_system_users_tagged && userInfo.personGuid.indexOf($FUSsystemUserPrefix)>=0)?'bg-surface-3 text-text-placeholder':'bg-btn-primary text-text-inverse'):
                       ((system_users_tagged && userInfo.personGuid.indexOf($FUSsystemUserPrefix)<0 || non_system_users_tagged && userInfo.personGuid.indexOf($FUSsystemUserPrefix)>=0)?'text-text-placeholder':'text-text-default')">
          {{ userInfo.fullName || userInfo.friendlyName }}
        </a>
      </div>
    </div>
    <!-- Emoji Container -->
    <div class="emoji_container relative">
      <div v-if="activate_emoji_window" class="emoji_tagging_suggest absolute w-full" v-show="show_emoji_window">
        <emoji-picker key="messenger_emoji_picker" :data="emoji_index" :set="emoji_set" :title="$t('messaging.input.selectEmoji')" :emojiSize="24" :skin="1" @select="insertContentObject('EMOJI', $event)">
          <span slot="searchTemplate" class="hidden"></span>
          <span slot="previewTemplate" class="hidden"></span>
        </emoji-picker>
      </div>
    </div>
    <!-- Update Context Container -->
    <div ref="dealer_context_popup" class="absolute px-2 py-3 bg-dealer-filter rounded-md shadow-[0px_2px_6px_0px_rgba(16,20,29,0.06)] outline outline-1 outline-offset-[-1px] outline-border-separator flex-col z-20 bottom-16 left-4" :class="show_context_options?'inline-flex':'hidden'">
      <div class="w-80 pb-3 flex flex-col justify-start items-start gap-1">
        <div class="justify-center text-text-default text-base font-semibold font-[family-name:var(--font-family-sans)] leading-5">{{ $t('messaging.input.dealershipFilter') }}</div>
        <div class="self-stretch justify-center text-text-default text-sm font-normal font-[family-name:var(--font-family-sans)] leading-4 tracking-tight">{{ $t('messaging.input.dealershipFilterDescription') }}</div>
      </div>
      <div class="max-h-48 overflow-y-auto flex flex-col">
        <!-- Groups section: "All rooftops" (the __ALL__ virtual group) plus the caller's usable
             dealer groups. A group selection sends DealerType='Group' + DealerId=<group id> on the
             message context (never DealerIdType='CIF' — see message_context in MessengerWindow). -->
        <template v-if="show_groups_section">
          <div class="self-stretch px-4 pb-1 text-text-subtle text-xs font-semibold leading-4">{{ $t('messaging.input.groups') }}</div>
          <div v-if="show_all_rooftops_option" @click.stop.prevent="selectAllRooftops()"
               class="self-stretch px-4 py-1 rounded-md flex flex-col justify-center items-start gap-0.5 hover:bg-surface-3 transition-colors cursor-pointer">
            <div class="self-stretch inline-flex justify-start items-center gap-2">
              <div class="w-7 h-7 p-1.5 rounded-[40px] flex justify-center items-center gap-2.5">
                <div class="w-4 h-4 p-1 rounded-[40px] outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5" :class="selectedGroupId === all_rooftops_group_id ? 'bg-btn-primary outline-brand-outline' : 'bg-white/0 outline-border-separator'">
                  <div v-if="selectedGroupId === all_rooftops_group_id" class="w-1.5 h-1.5 bg-surface-1 rounded-[40px]"></div>
                </div>
              </div>
              <div class="flex-1 justify-center text-text-default text-sm font-normal leading-4 tracking-tight">
                {{ $t('messaging.input.allRooftops') }} ({{ dealers.length }})
              </div>
            </div>
          </div>
          <div v-for="group in usable_groups" :key="group.id" @click.stop.prevent="selectGroup(group)"
               class="self-stretch px-4 py-1 rounded-md flex flex-col justify-center items-start gap-0.5 hover:bg-surface-3 transition-colors cursor-pointer">
            <div class="self-stretch inline-flex justify-start items-center gap-2">
              <div class="w-7 h-7 p-1.5 rounded-[40px] flex justify-center items-center gap-2.5">
                <div class="w-4 h-4 p-1 rounded-[40px] outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5" :class="selectedGroupId === group.id ? 'bg-btn-primary outline-brand-outline' : 'bg-white/0 outline-border-separator'">
                  <div v-if="selectedGroupId === group.id" class="w-1.5 h-1.5 bg-surface-1 rounded-[40px]"></div>
                </div>
              </div>
              <div class="flex-1 justify-center text-text-default text-sm font-normal leading-4 tracking-tight">
                {{ group.name }} ({{ group.accessibleCount }})
              </div>
            </div>
          </div>
          <div class="self-stretch px-4 pt-2 pb-1 text-text-subtle text-xs font-semibold leading-4">{{ $t('messaging.input.dealerships') }}</div>
        </template>
        <div v-for="dealer in [...dealers].sort((a, b) => (a.name || a.cifNumber || '').localeCompare(b.name || b.cifNumber || ''))" :key="dealer.cifNumber" @click.stop.prevent="selectDealer(dealer.cifNumber)"
             class="self-stretch px-4 py-1 rounded-md flex flex-col justify-center items-start gap-0.5 hover:bg-surface-3 transition-colors cursor-pointer">
          <div class="self-stretch inline-flex justify-start items-center gap-2">
            <div class="w-7 h-7 p-1.5 rounded-[40px] flex justify-center items-center gap-2.5">
              <div class="w-4 h-4 p-1 rounded-[40px] outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5" :class="!selectedGroupId && selectedDealerCif === dealer.cifNumber ? 'bg-btn-primary outline-brand-outline' : 'bg-white/0 outline-border-separator'">
                <div v-if="!selectedGroupId && selectedDealerCif === dealer.cifNumber" class="w-1.5 h-1.5 bg-surface-1 rounded-[40px]"></div>
              </div>
            </div>
            <div class="flex-1 justify-center text-text-default text-sm font-normal leading-4 tracking-tight">
              {{ dealer.name || $t('common.noName') }} ({{ dealer.cifNumber }})
            </div>
          </div>
        </div>
      </div>
      <!-- Manage my groups — opens the DealerGroupManager modal (User groups only). -->
      <div class="self-stretch pt-2 mt-1 border-t border-border-separator">
        <div class="px-4 py-1 rounded-md hover:bg-surface-3 transition-colors cursor-pointer inline-flex items-center gap-2" @click.stop.prevent="openGroupManager()">
          <i class="ri-settings-3-line ri-1x text-text-subtle"></i>
          <span class="text-text-link text-sm font-normal leading-4">{{ $t('messaging.groups.manage') }}</span>
        </div>
      </div>
    </div>
    <!-- Input Box -->
    <div ref="messenger_input_container" class="self-stretch flex flex-col items-center gap-2 relative" :class="['CREATE_MESSAGE','CREATE_CHANNEL'].indexOf(mode) >= 0 ? 'flex-1' : ''">
      <div class="self-stretch bg-surface-1/50 flex flex-col gap-2" :class="[mode === 'MESSENGER'?'justify-end':'justify-start', ['CREATE_MESSAGE','CREATE_CHANNEL'].indexOf(mode) >= 0 ? 'flex-1' : '']">
        <div class="self-stretch overflow-hidden relative"
             @dragenter.prevent="onComposerDragEnter" @dragover.prevent="onComposerDragEnter"
             @dragleave="onComposerDragLeave" @drop.prevent.stop="onComposerDrop"
             :class="[{'rounded-xl outline outline-1 outline-offset-[-1px] outline-border-default':['MESSENGER','WELCOME'].indexOf(mode) >= 0}, 
                      {'min-h-24 flex flex-col justify-end items-start':mode === 'MESSENGER'},
                      {'flex-1 flex flex-col justify-start items-start':['CREATE_MESSAGE','CREATE_CHANNEL'].indexOf(mode) >= 0}, 
                      {'inline-flex justify-start items-center':mode === 'WELCOME'}]">
          <!-- Drop target overlay: only while dragging files over the composer. Covers the whole
               composer rather than a dedicated strip, so the gesture works wherever the pointer lands. -->
          <div v-if="drop_active && attachments_enabled"
               class="absolute inset-0 z-20 rounded-xl border-2 border-dashed border-brand-outline bg-brand-subtle/80 flex justify-center items-center pointer-events-none">
            <div class="text-text-link text-sm font-semibold">{{ $t('messaging.input.dropToAttach') }}</div>
          </div>

          <!-- Staged attachments. Nothing here has been uploaded yet: the files leave the browser only
               when the message is sent (see uploadStagedFiles). -->
          <div v-if="attachments_enabled && $FUSarrayHasEntries(staged_files)" class="w-full px-4 pt-2.5 flex flex-wrap justify-start items-center gap-2">
            <div v-for="staged in staged_files" :key="staged.id"
                 class="max-w-56 pl-1 pr-1.5 py-1 bg-surface-2 rounded-lg inline-flex justify-start items-center gap-1.5 overflow-hidden">
              <img v-if="staged.is_image" :src="staged.url" class="w-6 h-6 rounded object-cover shrink-0" :alt="staged.name" />
              <i v-else-if="staged.is_media" class="ri-mic-2-line ri-125 text-text-subtle shrink-0" :title="$t('messaging.input.transcribedAfterUpload')" />
              <i v-else class="ri-file-text-line ri-125 text-text-subtle shrink-0" />
              <span class="text-text-default text-xs font-normal leading-4 truncate" :title="staged.name">{{ staged.name }}</span>
              <span v-if="staged.upload_state === 'uploading'" class="text-text-subtle text-[10px] shrink-0">
                {{ $t('messaging.input.sending') }}
              </span>
              <span v-else-if="staged.upload_state === 'failed'" class="text-danger text-[10px] shrink-0">{{ $t('messaging.input.notSent') }}</span>
              <span v-else-if="staged.extract_state === 'working'" class="text-text-subtle text-[10px] shrink-0">
                {{ staged.extract_label || $t('messaging.input.reading') }}
              </span>
              <span v-else-if="staged.extract_state === 'failed'" class="text-danger text-[10px] shrink-0">{{ $t('messaging.input.unreadable') }}</span>
              <!-- Share this file with everyone at the store, decided BEFORE it is uploaded so it is
                   filed in the right place the first time. Same icon as the promote button on a sent
                   attachment, because it is the same action taken earlier. Off unless clicked: putting
                   a document into the store's corpus is not the safe assumption, and a chip nobody
                   looked at must not publish someone's file. -->
              <i v-if="share_on_upload_enabled && !staged.is_image && staged.upload_state !== 'uploading'"
                 class="ri-125 cursor-pointer shrink-0"
                 :class="staged.share_with_dealership
                   ? 'ri-building-2-fill text-text-link'
                   : 'ri-building-2-line text-text-subtle hover:text-text-link'"
                 :aria-pressed="staged.share_with_dealership ? 'true' : 'false'"
                 :data-test="`share-${staged.id}`"
                 :title="shareToggleTitle(staged)"
                 @click.stop="toggleShare(staged.id)" />
              <i class="ri-close-line ri-125 text-text-subtle cursor-pointer hover:text-danger shrink-0"
                 :title="$t('messaging.input.removeAttachment')" @click.stop="removeStagedFile(staged.id)" />
            </div>
          </div>

          <div v-if="attachments_enabled && stage_error" class="w-full px-4 pt-2 text-danger text-xs font-normal leading-4">
            {{ stage_error }}
          </div>

          <!-- Something worth saying that is NOT a problem — e.g. a long recording's audio was split
               into parts. Kept separate from stage_error because showing a neutral fact in red reads as
               a failure, and the user then looks for what went wrong. -->
          <div v-if="attachments_enabled && stage_notice" class="w-full px-4 pt-2 text-text-subtle text-xs font-normal leading-4">
            {{ stage_notice }}
          </div>

          <div class="w-full pb-2 flex justify-start items-start cursor-text" :class="['CREATE_MESSAGE','CREATE_CHANNEL'].indexOf(mode) >= 0?'flex-1':''" @click="focusMessengerInput">
            <div class="flex-1 px-4 py-2.5 flex justify-start items-center gap-2.5">
              <div ref="messenger_input" class="messenger_input input-area flex-1 justify-start text-text-subtle text-sm font-normal leading-4 tracking-tight"
                   :contenteditable="messageInputReady === null?message_send_ready:messageInputReady" role="textbox" spellcheck="true" :data-placeholder="input_placehoder || $t('messaging.input.askPlaceholder')"
                   @input="handleTyping" @beforeinput="fixInvalidSelection" @keydown.esc="escMessage" @keydown.enter.stop.prevent="sendMessage()" @keydown.tab="selectActiveTaggedUser" tabindex="100"
                   @keydown.down="setActiveTaggedUser($event, true)" @keydown.up="setActiveTaggedUser($event, false)" @focus="handleInputFocus" @blur="removeKeyboardSpacing"
                   @keydown.left="handleArrowLeftRight" @keydown.right="handleArrowLeftRight" @keydown.delete="handleBackspace" @paste.prevent="handlePaste" @drop.prevent.stop="" />
            </div>
          </div>
          <div v-if="mode === 'WELCOME'" class="h-12 pr-2 py-2.5 flex justify-start items-center gap-1.5 mt-auto mb-2">
            <div v-if="false" class="w-8 h-8 p-0.5 bg-transparent rounded-md flex justify-center items-center gap-2.5">
              <div v-if="uploading_file" class="mx-2 fusion-loader loader-md" />
              <i v-else class="mx-2 ri-125 text-text-subtle" :class="'ri-attachment-2'+(message_send_ready?' cursor-pointer hover:text-text-link':'')" :title="$t('messaging.input.attachFile')" @click="openAttachment" />
            </div>
            <!-- TTS Playback Control (Welcome mode) -->
            <div v-if="isTtsPlaying" class="w-8 h-8 p-0.5 bg-transparent rounded-md flex justify-center items-center gap-2.5">
              <i class="mx-2 ri-volume-up-line ri-125 text-text-link animate-pulse cursor-pointer hover:text-danger" 
                 @click="stopTts" 
                 :title="$t('messaging.input.stopPlayback')" />
            </div>
            <!-- Attach (+): staged only, uploaded on send. Placed left of the mic so the two input
                 affordances sit together, matching what people expect from other assistants. -->
            <div v-if="attachments_enabled" class="w-8 h-8 p-0.5 bg-transparent rounded-md flex justify-center items-center gap-2.5">
              <div v-if="uploading_file" class="mx-2 fusion-loader loader-md" />
              <label v-else :for="attachment_input_id" class="mx-2 inline-flex justify-center items-center"
                     :class="message_send_ready ? 'cursor-pointer' : 'pointer-events-none opacity-60'"
                     :title="$t('messaging.input.attachDocument')" @click="onAttachmentTriggerClick">
                <i class="ri-add-line ri-125 text-text-subtle"
                   :class="message_send_ready ? 'hover:text-text-link' : ''" />
              </label>
            </div>
            <!-- Mic Button (Welcome mode) -->
            <div class="w-8 h-8 p-0.5 bg-transparent rounded-md flex justify-center items-center gap-2.5">
              <div v-if="isTranscribing" class="mx-2 fusion-loader loader-md" />
              <i v-else class="mx-2 ri-125"
                 :class="[
                   isRecording ? 'ri-stop-circle-line text-danger animate-pulse cursor-pointer' : 'ri-mic-line text-text-subtle',
                   message_send_ready && !isRecording ? 'cursor-pointer hover:text-text-link' : ''
                 ]"
                 :title="isRecording ? $t('messaging.input.stopRecording') : $t('messaging.input.voiceInput')"
                 @click="toggleRecording" />
            </div>
          </div>
          <div v-if="mode === 'MESSENGER'" class="self-stretch h-12 pr-2 py-2.5 inline-flex justify-end items-center gap-1.5 relative">
            <!-- The store picker and the open topic's name share ONE flex-1 cell, so the send/mic
                 group on the right stays pinned where it was. The picker keeps its own click target
                 (now shrink-wrapped to the pill) rather than owning the whole cell: the topic beside
                 it is a label, and clicking it must not open the dealership list. -->
            <div v-if="isSystemAi" class="flex-1 min-w-0 pl-2 flex justify-start items-center gap-2">
              <div ref="dealer_context_trigger" class="shrink-0 flex justify-start items-start gap-2.5 cursor-pointer" :title="$t('messaging.input.changeDealershipFilter')" @click="show_context_options=!Boolean(show_context_options)">
                <div class="px-2 py-1 bg-surface-2 rounded-xl flex justify-start items-center gap-1 overflow-hidden">
                  <div v-if="false" class="flex justify-start items-center gap-2.5">
                    <div class="w-3.5 h-3.5 bg-surface-1 rounded-full"></div>
                    <img class="w-3 h-0.5" src="https://placehold.co/12x2" />
                  </div>
                  <div class="justify-center text-text-default text-[10px] font-normal leading-3">{{ active_dealer_label }}</div>
                </div>
              </div>
              <!-- Which topic the next question lands in. Rey names the open conversation only in the
                   left-hand list, which is not on screen in the toolbar layout — so without this there
                   is nothing saying where a message is about to go. min-w-0 here AND on the cell above
                   is what lets the ellipsis happen at all: a flex item's default min-width is auto,
                   which refuses to shrink below its text and would push the send button off instead.
                   The title attribute is how a clipped name stays readable. -->
              <div v-if="topic_label" class="rey-composer-topic flex-1 min-w-0 truncate text-text-placeholder text-[10px] font-normal leading-3" :title="topic_label">{{ topic_label }}</div>
            </div>
            <div class="h-8 relative" :class="isSystemAi?'':'w-full'">
              <div class="h-8" :class="isSystemAi?'flex':'w-full flex justify-start pl-2'">
                <div v-if="!isSystemAi" class="w-8 h-8 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
                  <i class="ri-at-line ri-125 text-text-subtle" :class="{'cursor-pointer hover:text-text-link':message_send_ready}" :title="$t('messaging.input.mentionUser')" @click="showUserTagging" />
                </div>
                <div v-if="false" class="w-8 h-8 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
                  <div v-if="uploading_file" class="fusion-loader loader-md" />
                  <i v-else class="ri-125 text-text-subtle" :class="'ri-attachment-2'+(message_send_ready?' cursor-pointer hover:text-text-link':'')" :title="$t('messaging.input.attachFile')" @click="openAttachment" />
                </div>
                <div v-if="false && !isSystemAi" class="w-8 h-8 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
                  <i class="ri-emotion-happy-line ri-125 text-text-subtle" :class="{'cursor-pointer hover:text-text-link':message_send_ready}" tabindex="105" @click="toggleEmojis" />
                </div>
                <!-- TTS Playback Control -->
                <div v-if="isSystemAi && isTtsPlaying" class="w-8 h-8 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
                  <i class="ri-volume-up-line ri-125 text-text-link animate-pulse cursor-pointer hover:text-danger" 
                     @click="stopTts" 
                     :title="$t('messaging.input.stopPlayback')" />
                </div>
                <!-- Attach (+): staged only, uploaded on send. Placed left of the mic so the two
                     input affordances sit together, matching other assistants. -->
                <div v-if="attachments_enabled" class="w-8 h-8 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
                  <div v-if="uploading_file" class="fusion-loader loader-md" />
                  <label v-else :for="attachment_input_id" class="inline-flex justify-center items-center"
                         :class="message_send_ready ? 'cursor-pointer' : 'pointer-events-none opacity-60'"
                         :title="$t('messaging.input.attachDocument')" @click="onAttachmentTriggerClick">
                    <i class="ri-add-line ri-125 text-text-subtle"
                       :class="message_send_ready ? 'hover:text-text-link' : ''" />
                  </label>
                </div>
                <!-- Mic Button -->
                <div v-if="isSystemAi" class="w-8 h-8 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
                  <div v-if="isTranscribing" class="fusion-loader loader-md" />
                  <i v-else class="ri-125"
                     :class="[
                       isRecording ? 'ri-stop-circle-line text-danger animate-pulse cursor-pointer' : 'ri-mic-line text-text-subtle',
                       message_send_ready && !isRecording ? 'cursor-pointer hover:text-text-link' : ''
                     ]"
                     :title="isRecording ? $t('messaging.input.stopRecording') : $t('messaging.input.voiceInput')"
                     @click="toggleRecording" />
                </div>
                <div class="w-8 h-8 justify-center items-center gap-2.5" :class="[(messageStreaming?'hidden':'inline-flex'), {'ml-auto': !isSystemAi}]">
                  <i class="ri-send-plane-2-fill ri-125" :class="message_send_ready?'cursor-pointer text-text-link hover:text-text-link':'text-text-placeholder'" :title="$t('common.send')" @click="sendMessage(true)" @keydown.enter.stop.prevent="sendMessage(true)" />
                </div>
                <div v-if="isSystemAi" class="w-8 h-8 justify-center items-center gap-2.5" :class="messageStreaming?'inline-flex':'hidden'">
                  <div class="w-full h-full rounded-full flex justify-center items-center border border-solid bg-brand-subtle border-brand-outline cursor-pointer hover:bg-brand-subtle" :title="$t('messaging.input.stopGenerating')" @click="cancelMessage()">
                    <i class="ri-square-fill ri-sm text-text-link" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="['CREATE_MESSAGE', 'CREATE_CHANNEL'].indexOf(mode) >= 0" class="self-stretch h-16 px-2 inline-flex justify-end items-center gap-1.5 relative border-t border-solid border-border-separator">
            <div class="w-16 h-10 flex justify-center items-center gap-1.5 rounded-md" :class="message_send_ready?'bg-btn-primary hover:bg-btn-primary/80 cursor-pointer':'bg-btn-secondary'" :title="mode === 'CREATE_CHANNEL' ? $t('messaging.input.createChannel') : $t('common.send')" @click="sendMessage(true)" @keydown.enter.stop.prevent="sendMessage(true)">
              <i v-if="mode === 'CREATE_MESSAGE'" class="ri-send-plane-2-fill ri-125 text-text-link" />
              <i v-if="mode === 'CREATE_CHANNEL'" class="ri-add-fill ri-125 text-text-link" />
            </div>
            <div v-if="false && mode === 'CREATE_MESSAGE'" class="w-8 h-8 p-0.5 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
              <div v-if="uploading_file" class="fusion-loader loader-md" />
              <i v-else class="ri-125 text-text-subtle" :class="'ri-attachment-2'+(message_send_ready?' cursor-pointer hover:text-text-link':'')" :title="$t('messaging.input.attachFile')" @click="openAttachment" />
            </div>
            <div v-if="false && !isSystemAi" class="w-8 h-8 p-0.5 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
              <i class="ri-emotion-happy-line ri-125 text-text-subtle" :class="{'cursor-pointer hover:text-text-link':message_send_ready}" tabindex="105" @click="toggleEmojis" />
            </div>
            <div v-if="mode === 'CREATE_MESSAGE'" class="w-8 h-8 p-0.5 bg-transparent rounded-md inline-flex justify-center items-center gap-2.5">
              <i class="ri-at-line ri-125 text-text-subtle" :class="{'cursor-pointer hover:text-text-link':message_send_ready}" :title="$t('messaging.input.mentionUser')" @click="showUserTagging" />
            </div>
            <div class="w-8 h-8 p-0.5 inline-flex justify-center items-center gap-2.5 ml-auto">
              <i class="ri-delete-bin-line ri-125 text-text-subtle hover:text-text-link cursor-pointer" tabindex="106" :title="$t('messaging.input.discard')" @click="cancelMessage" />
            </div>
          </div>
        </div>
      </div>
      <!-- End Input Box -->
      <!-- AI Disclosure -->
      <div v-if="mode === 'WELCOME' || (mode === 'MESSENGER' && isSystemAi)" class="text-center justify-start text-text-subtle text-[10px] leading-3 tracking-tight">
        <!-- ENGLISH-ONLY, deliberately not wired to $t(): this is AI disclosure copy, held in English
             (along with the disclosure popup below) pending legal sign-off. Localize only when the
             five per-platform disclaimers in ai_disclosure_text are translated and signed off. -->
        <template v-if="mode === 'MESSENGER'">
          Rey is an <span class="select-none" @dblclick="resetThread()">AI agent</span><span v-if="processing_thread_reset" class="inline-block fusion-loader loader-sm ml-1 align-middle" /> | Verify for accuracy
          <template v-if="ai_disclosure_text">| <span class="underline cursor-pointer" @click="openAiDisclosureModal()">Learn more</span></template>
        </template>
        <template v-else-if="mode === 'WELCOME'">Rey utilizes AI in its responses.<br/>Conversations are not monitored by human agents.</template>
      </div>
      <!-- Typing Indicator -->
      <div v-if="mode === 'MESSENGER'" class="text-text-subtle text-[10px] leading-3 tracking-tight pb-1 mr-auto" :class="{'invisible':!$FUSarrayHasEntries(typingUsers)}">
        <span class="font-bold">{{
          typingUsers.map(userId => {
            return ent_users_by_guid[userId]?(ent_users_by_guid[userId].friendlyName || ent_users_by_guid[userId].fullName):$FUSresolveGhostUser(userId).friendlyName;
          }).join(", ")
        }}</span>
        {{ $t('messaging.input.typing', typingUsers.length) }}
      </div>
    </div>
    <!-- Attachment picker for the Rey composer. A native input rather than FilePond's browse():
         vueFilePond() is constructed with no plugins here and none are installed, so its
         accepted-file-types attribute validates nothing — it would contribute only a file dialog.
         Validation lives in stageFiles and keys on the EXTENSION, which is what the server switches
         on (ThreadAttachmentExtractor.ImageExtensions / SourceFormatFor). -->
    <!-- Not class="hidden" (display:none). A <label for> paired with the input is what actually
         opens the dialog, and a display:none input is the one shape browsers have historically
         refused to open. Kept off-screen but laid out so the pairing always works. -->
    <input :id="attachment_input_id" ref="rey_attachment_input" type="file" multiple
           class="absolute w-0 h-0 opacity-0 overflow-hidden -z-10"
           :accept="accepted_attachment_extensions" @change="onAttachmentInputChange" />

    <!-- Upload Container -->
    <div ref="messenger_upload_container" class="messenger_upload_container">
      <file-pond key="messenger_upload_file_pond" id="messenger_upload_file_pond" ref="messenger_upload_file_pond" class="mb-0 hidden" name="File" :allow-drop="false" :allow-multiple="true"
                 :allow-browse="true" accepted-file-types="image/*" :allow-revert="false" credits="" maxParallelUploads="1"
                 :server="{ process: filePondProcess }" />
      <div v-if="false" v-show="$FUSarrayHasEntries(upload_preview_files)" class="messenger_upload_file_list flex justify-start w-full py-2 pr-2">
        <template v-for="uploadPreview in upload_preview_files">
          <div v-if="uploadPreview.url" class="relative ml-2" style="height: 62px">
            <div v-if="uploadPreview.status==='UPLOADING'" class="fusion-loader loader-sm absolute" style="top: 0; right: -4px" />
            <i v-else-if="uploadPreview.status==='ERROR'" class="ri-sm absolute text-danger ri-alert-fill" style="top: 0; right: -4px" />
            <img v-if="uploadPreview.type && uploadPreview.type.indexOf('image')>=0" :src="uploadPreview.url" class="img-fluid" style="max-height: 62px" :style="uploadPreview.status==='UPLOADING'?'opacity: 80%':''" />
            <span class="px-2 border border-border-separator flex items-center h-full">{{ uploadPreview.name }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
  
  <!-- The AI disclosure popup stays ENGLISH-ONLY for now, title included — deliberately NOT wired to
       $t(). Its body (ai_disclosure_text) is the per-platform AI legal text, which is held in English
       pending legal sign-off; a translated title over untranslated legal copy would read as a
       half-localized disclosure. Localize the title only when the five disclaimers are translated
       and signed off together. -->
  <dialog-modal :is-open="show_ai_disclosure_modal" title="AI Disclosure" :show-close-button="true" @close="show_ai_disclosure_modal = false">
    <p class="text-text-default text-sm leading-5">{{ ai_disclosure_text }}</p>
  </dialog-modal>
  <dealer-group-manager :is-open="show_group_manager" @close="show_group_manager = false" @groupDeleted="handleGroupDeleted" />
</template>

<style scoped>
#messenger_message_box :deep(.tagged-user) {
  font-weight: bold;
  color: var(--link-primary);
  background-color: var(--background-brand-subtle);
  padding: 2px 5px;
  border-radius: 3px;
  user-select: none;
}

#messenger_message_box .input-area {
  overflow-wrap: anywhere;
  overflow-y: auto;
}

.input-area {
  outline: none;
  font-size: 14px;
  cursor: text;
}

.input-area:empty:before {
  content: attr(data-placeholder);
  color: var(--text-placeholder);
  pointer-events: none;
}

/* Wrapped lines inside the composer. 1.25rem is leading-5, which is what text-sm is set to elsewhere in
   this file, and it sits next to the leading-4 the input-area element declares for itself — so a
   message that wraps reads at the same rhythm as the rest of the composer.

   Was 1.7rem: at 14px text that is a ratio of nearly 2, which passed unnoticed while the composer held
   a single typed line and only became visible once a wrapped one was seeded into it. */
.input-area :deep(p) {
  line-height: 1.25rem;
}

.input-area :deep(.emoji-text) {
  width: 24px;
  height: 24px;
  display: inline-block;
  background-size: 6100%;
  background-image: url('@images/emojis_32.png');
  vertical-align: bottom;
}

.user_tagging_suggest_container {
  height: 2px;
}

.user_tagging_suggest {
  max-width: 340px;
  max-height: 260px;
  overflow: auto;
  z-index: 1051;
  bottom: 2px;
}

.tagging-open-down .user_tagging_suggest {
  bottom: auto;
  top: 2px;
}

.emoji_container {
  height: 2px;
}

.emoji_tagging_suggest {
  max-width: 340px;
  max-height: 420px;
  overflow: auto;
  z-index: 1051;
  bottom: 2px;
}

.emoji_tagging_suggest :deep(.emoji-type-image.emoji-set-apple) {
  background-image: url('@images/emojis_32.png');
}

.emoji_tagging_suggest :deep(.emoji-type-image.emoji-set-facebook) {
  background-image: url('@images/emojis_32.png');
}

.emoji_tagging_suggest :deep(.emoji-type-image.emoji-set-google) {
  background-image: url('@images/emojis_32.png');
}

.emoji_tagging_suggest :deep(.emoji-type-image.emoji-set-twitter) {
  background-image: url('@images/emojis_32.png');
}

.context-options-container {
  max-width: 340px;
  bottom: 60px;
  left: 8px;
  background-color: var(--background-layer-01);
  box-shadow: 0px 4px 24px rgba(16, 20, 29, 0.06), 0px 2px 6px rgba(16, 20, 29, 0.06);
  border-radius: 6px;
  border: 1px solid var(--border-default);
  box-sizing: border-box;
}
</style>

<script>
import { Picker, EmojiIndex } from "emoji-mart-vue-fast";
import data from "emoji-mart-vue-fast/data/all.json";
// const emojiIndex = new EmojiIndex(data);
import "emoji-mart-vue-fast/css/emoji-mart.css";
import vueFilePond from 'vue-filepond';
import 'filepond/dist/filepond.min.css';
import emojiSpritesheet from "@images/emojis_32.png";
import {mapStores} from "pinia";
import {useMainStore} from "@store/main";
import DialogModal from "@components/UI/DialogModal.vue";
import DealerGroupManager from "@components/Messaging/DealerGroupManager.vue";
import { extractMedia } from '../../utils/mediaExtraction';
import { ALL_ROOFTOPS_GROUP_ID } from '../../utils/dealerGroups';

const FilePond = vueFilePond();

export default {
  name: 'MessageInput',
  components: {
    'emoji-picker': Picker,
    'file-pond': FilePond,
    'dialog-modal': DialogModal,
    'dealer-group-manager': DealerGroupManager,
  },
  props: {
    mode: {
      type: String,
      required: false,
      default: 'MESSENGER',
      validator: (value) => ['MESSENGER', 'WELCOME', 'CREATE_MESSAGE', 'CREATE_CHANNEL'].includes(value)
    },
    inputConfig: {
      type: Object,
      required: false,
      default: null
    },
    isSystemAi: {
      type: Boolean,
      required: false,
      default: false
    },
    messageStreaming: {
      type: Boolean,
      required: false,
      default: false
    },
    messageSendReady: {
      type: Boolean,
      required: false,
      default: true
    },
    messageInputReady: {
      type: Boolean,
      required: false,
      default: null
    },
    usersInfo: {
      type: Object,
      required: false,
      default: null
    },
    typingUsers: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    messageUser: {
      type: String,
      required: false,
      default: null
    },
    clearMessengerInput: {
      type: Number,
      required: false,
      default: 0
    },
    processingMessage: {
      type: String,
      required: false,
      default: null
    },
    selectedDealerCif: {
      type: String,
      required: false,
      default: null
    },
    // Id of the selected dealer group ('__ALL__' for the all-rooftops virtual group). When set it
    // supersedes selectedDealerCif in the picker's radio state and the filter label.
    selectedGroupId: {
      type: String,
      required: false,
      default: null
    },
    emojiIndexObj: {
      type: Object,
      required: false,
      default: null
    },
    threadId: {
      type: String,
      required: false,
      default: null
    },
    // Display name of the open Rey topic, already resolved by the parent (placeholder labels and the
    // legacy thread's own label are decided there, next to the conversation list that shows the same
    // string). Empty on every thread that is not a titled topic, which is what hides the label.
    topicName: {
      type: String,
      required: false,
      default: null
    },
    participantGuids: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
  },
  mounted() {
    document.addEventListener('selectionchange', this.handleSelectionChange);
    document.addEventListener('click', this.handleDealerListClickOutside);
    window.addEventListener('keydown', this.handleKeyDownEvent);
    window.addEventListener('keyup', this.handleKeyUpEvent);
    window.addEventListener("pointerup", this.fixInvalidSelection);
    this.refreshAttachmentAvailability();
    // Dealer groups feed the picker's Groups section; only the system-AI composer shows the
    // dealership filter. Cached in the store, so repeated mounts don't refetch.
    if (this.isSystemAi)
      this.fetchDealerGroups();
  },
  beforeUnmount() {
    // stopTts()/ttsAudioCache reset previously lived in a beforeDestroy hook, which Vue 3 never
    // calls — so TTS audio kept playing and the cache leaked on unmount. Merged here.
    this.stopTts();
    this.ttsAudioCache = {};
    document.removeEventListener('selectionchange', this.handleSelectionChange);
    document.removeEventListener('click', this.handleDealerListClickOutside);
    window.removeEventListener('keydown', this.handleKeyDownEvent);
    window.removeEventListener('keyup', this.handleKeyUpEvent);
    window.removeEventListener("pointerup", this.fixInvalidSelection);
  },
  data() {
    return {
      can_show_tagged_users: false,
      activate_emoji_window: false,
      show_emoji_window: false,
      emoji_set: 'apple',
      cursor_within_input: false,
      current_tagged_users: [],
      active_tagged_user: null,
      active_range: null,
      new_message: '',
      shift_down: false,
      new_message_cursor_position: -1,
      uploading_file: false,
      show_context_options: false,
      upload_preview_files: [],
      // Attachments staged in the composer. Held in the browser only — they are uploaded when the
      // message is sent, so a file that gets removed never reaches the server at all.
      staged_files: [],
      staged_file_seq: 0,
      // Unique per instance: MessengerInput is mounted more than once in some layouts, and two inputs
      // sharing an id would make every label open the first one's dialog.
      attachment_input_id: `rey-attach-${Math.random().toString(36).slice(2, 10)}`,
      drop_active: false,
      drop_depth: 0,
      stage_error: '',
      stage_notice: '',
      // Voice recording state
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      audioContext: null,
      analyser: null,
      silenceTimer: null,
      silenceDelay: 1500, // ms of silence before auto-stop
      silenceThreshold: 0.03,
      speechDetected: false,
      maxRecordingTimeout: null,
      maxRecordingDuration: 120000, // 2 min hard cap
      processing_thread_reset: false,
      voiceWarning: null,
      isTranscribing: false,
      // TTS playback state
      ttsAudio: null,       // AudioContext for streaming playback
      ttsAbort: null,       // AbortController for cancelling in-flight fetch
      isTtsPlaying: false,
      ttsTurnId: null,
      ttsQueue: [],
      ttsAudioCache: {},    // Float32Array PCM cache keyed by text hash
      autoSendOnTranscribe: true, // Auto-send voice messages
      show_ai_disclosure_modal: false,
      show_group_manager: false,
    };
  },
  computed: {
    ...mapStores(useMainStore),
    // Rey's composer only. The attachment buttons written for the other modes stay behind their
    // existing v-if="false" — turning those on is a separate product decision, not a side effect.
    //
    // WHERE the picker could appear, not whether it may. Kept separate because it is what decides that
    // asking the server is worthwhile, and folding the answer into it would deadlock: the question would
    // gate the request that answers it.
    attachments_in_reys_composer() {
      return Boolean(this.isSystemAi) && ['MESSENGER', 'WELCOME'].indexOf(this.mode) >= 0;
    },
    attachments_availability() {
      return this.mainStore.attachments_availability;
    },
    /**
     * Whether the server has said this caller may attach files.
     *
     * Fail-CLOSED, and deliberately the opposite of attachments_denied_reason below, which reads a
     * missing answer as allowed. The two are answering different questions: that one decides whether to
     * refuse a file the moment it is picked, where guessing "denied" would be wrong; this one decides
     * whether to offer uploading at all, where an unanswered question must not put a control on screen
     * for a feature nobody is being offered.
     *
     * The upload endpoint remains the authority either way — this only changes what is shown.
     */
    attachments_available() {
      return this.attachments_availability?.enabled === true;
    },
    // Both halves: the right surface, and permission to be there. Every existing reader of this — the
    // buttons, the drop zone, the paste handler, the send path — gets the feature gate for free, which
    // is why the answer is folded in here rather than added at each of them and one being missed.
    attachments_enabled() {
      return this.attachments_in_reys_composer && this.attachments_available;
    },
    /**
     * Whether to offer "share this with the store" on a staged file.
     *
     * Must match the server's FUSION_SHARE_ON_UPLOAD. The server enforces the flag independently, so a
     * mismatch cannot publish anything it should not — and if this is on while the server's is off, the
     * upload comes back with a notice saying the file stayed private rather than failing silently.
     *
     * There is no store to share TO without a CIF in context, so the control hides then as well:
     * guessing one would put a user's document into a dealership they are not even looking at.
     */
    share_on_upload_enabled() {
      return String(import.meta.env.VITE_SHARE_ON_UPLOAD).toLowerCase() === 'true'
        && !!this.current_cif;
    },

    current_cif() {
      return this.mainStore.current_cif;
    },

    /**
     * Why this store cannot attach files, or null when it can.
     *
     * Reads as ALLOWED whenever the answer is missing — not asked yet, request failed, no CIF in context.
     * Deliberate: see LoadAttachmentAvailability. The picker also stays enabled either way; this only
     * decides whether picking a file refuses immediately instead of at send.
     */
    attachments_denied_reason() {
      const availability = this.attachments_availability;
      if (!availability || availability.enabled !== false) return null;
      // The sentence comes from the server, so there is one copy of it and the refusal here reads exactly
      // like the one the upload endpoint answers with. The fallback is deliberately NOT that sentence
      // reproduced: a second copy would drift, and it only fires if a future response says "off" without
      // saying why — in which case a shorter, plainly generic line is the honest thing to show.
      return availability.reason || this.$t('messaging.input.attachmentsUnavailable');
    },

    // Exactly what the server can read, as extensions. An open picker would let a .zip through, and
    // SourceFormatFor maps every unmapped extension to PlainText — it would be ingested as a document
    // whose text is garbage, with nothing reporting it.
    //
    // Legacy Office (.doc, .xls, .ppt) is deliberately ABSENT: those are pre-2007 binary formats with
    // no extractor, and reading them needs a third-party library. They are named in the refusal
    // message so the user knows to save as the modern format rather than wondering why.
    accepted_attachment_extensions() {
      return this.image_attachment_extensions
        .concat(this.document_attachment_extensions)
        .concat(this.media_attachment_extensions)
        .join(',');
    },
    document_attachment_extensions() {
      return ['.pdf', '.docx', '.xlsx', '.pptx', '.html', '.htm', '.srt', '.vtt',
              '.txt', '.md', '.csv', '.json', '.log'];
    },
    // Audio and video are transcribed server-side. They carry their own size ceiling because a
    // recording is orders of magnitude larger than a document.
    media_attachment_extensions() {
      return ['.mp3', '.wav', '.m4a', '.ogg', '.webm', '.mp4', '.mov'];
    },
    // Named so the refusal can say "save it as .docx" instead of just "can't read this".
    legacy_office_extensions() {
      return { '.doc': '.docx', '.xls': '.xlsx', '.ppt': '.pptx' };
    },
    // 25 MB for documents, 200 MB for media. One number would either reject a short recording or let
    // a 200 MB spreadsheet through; the limit follows what the file actually is. Both are enforced
    // here so the user is told when they attach, not by an opaque 413 when they press send.
    max_document_bytes() {
      return 25 * 1024 * 1024;
    },
    max_media_bytes() {
      return 200 * 1024 * 1024;
    },
    // Mirrors ThreadAttachmentExtractor.ImageExtensions. Kept as its own list because the image cap
    // below applies to these and only these.
    image_attachment_extensions() {
      return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'];
    },
    // Named rather than inlined at the send site so the rule can be asserted on its own: jsdom has no
    // innerText, so extractRawMessage cannot run in a unit test and sendMessage is unreachable there.
    extraction_in_flight() {
      return this.staged_files.some(f => f.extract_state === 'working');
    },
    staged_image_count() {
      return this.staged_files.filter(f => f.is_image).length;
    },
    // Mirrors ThreadAttachmentResolver.MaxImages. If that constant moves, this has to move with it —
    // the two disagreeing means the UI promises something the model never sees.
    max_staged_images() {
      return 4;
    },
    user_access_info() {
      return this.mainStore.user_access_info;
    },
    wingman_config() {
      return this.mainStore.wingman_config;
    },
    ai_disclosure_text() {
      const system = this.wingman_config?.FusionInitType?.InitSystem?.toLowerCase();
      if (system === 'focus')
        return 'You acknowledge that the certain features provided by Reynolds as part of the FOCUS services (the "FOCUS Services") may incorporate artificial intelligence and machine learning technologies ("AI"), including large language models, to process and analyze information provided to it and to generate responses based on that information. Some responses are generated automatically and may be imprecise, incomplete, incorrect or out of date. You acknowledge that AI responses do not provide reliable professional, legal, financial, or other regulated advice, and any information or content provided by AI is for your general informational purposes only. You should not rely on any AI response as a substitute for professional advice, independent judgment, or independent verification. The FOCUS Services may collect, process, and analyze the content of your inputs and the associated interaction data to operate, maintain, improve, and develop (1) Reynolds\' services; and (2) the underlying AI models. Unless necessary for the intended use of a feature of the FOCUS Services, do not share confidential, proprietary, personal, or other sensitive information with the AI feature(s). You understand that your inputs and the AI outputs may be processed by third-party service providers and may be transferred, stored, or processed in other jurisdictions, which may have different data protection laws than your jurisdiction. By accessing the FOCUS Services, you agree to abide by these terms, the applicable terms of use, and privacy policy for each feature of the FOCUS Services that employs AI.';
      if (system === 'autovision')
        return 'You acknowledge that certain features provided on the AutoVision platform (the "Platform") may incorporate artificial intelligence and machine learning technologies ("AI"), including large language models, to process and analyze information provided to it and to generate responses based on that information. Some outputs are generated automatically and may be imprecise, incomplete, incorrect or out of date. You acknowledge that AI responses do not provide reliable professional, legal, financial or other regulated advice, and any information or content provided by AI is for your general informational purposes only. You should not rely on any AI response as a substitute for professional advice, independent judgment, or independent verification. The Platform may collect, process, and analyze the content of data inputs and the associated interaction data to operate, maintain, improve, and develop (1) the Platform; and (2) the underlying AI models. Unless necessary for the intended use of an applicable feature on the Platform, do not share confidential, proprietary, personal, or other sensitive information with the AI feature(s). You understand that inputs and the AI outputs may be processed by third-party service providers and may be transferred, stored, or processed in other jurisdictions, which may have different data protection laws than your jurisdiction. You agree to abide by the applicable terms of use and/or privacy policy for each feature employing AI.';
      if (system === 'reverserisk')
        return 'You acknowledge that certain features provided on the ReverseRisk platform (the "Platform") may incorporate artificial intelligence and machine learning technologies ("AI"), including large language models, to process and analyze the information provided to the AI feature and to generate responses based on that information. Some outputs are generated automatically and may be imprecise, incomplete, incorrect or out of date. You acknowledge that AI responses do not provide reliable professional, legal, financial or other regulated advice, and any information or content provided by AI is for your general informational purposes only. You should not rely on any AI response as a substitute for professional advice, independent judgment, or independent verification. The Platform may collect, process, and analyze the content of data inputs and the associated interaction data to operate, maintain, improve, and develop (1) the Platform; and (2) the underlying AI models. Unless necessary for the intended use of an applicable feature on the Platform, do not share confidential, proprietary, personal, or other sensitive information with the AI feature(s). You understand that inputs and the AI outputs may be processed by third-party service providers and may be transferred, stored, or processed in other jurisdictions, which may have different data protection laws than your jurisdiction. You agree to abide by the applicable terms of use and/or privacy policy for each feature employing AI.';
      if (system === 'reconvision')
        return 'You acknowledge that certain features provided on the ReconVision platform (the "Platform") may incorporate artificial intelligence and machine learning technologies ("AI"), including large language models, to process and analyze information provided to the AI feature and to generate responses based on that information. Some outputs are generated automatically and may be imprecise, incomplete, incorrect or out of date. You acknowledge that AI responses do not provide reliable professional, legal, financial or other regulated advice, and any information or content provided by AI is for your general informational purposes only. You should not rely on any AI response as a substitute for professional advice, independent judgment, or independent verification. The Platform may collect, process, and analyze the content of data inputs and the associated interaction data to operate, maintain, improve, and develop (1) the Platform; and (2) the underlying AI models. Unless necessary for the intended use of an applicable feature on the Platform, do not share confidential, proprietary, personal, or other sensitive information with the AI feature(s). You understand that inputs and the AI outputs may be processed by third-party service providers and may be transferred, stored, or processed in other jurisdictions, which may have different data protection laws than your jurisdiction. You agree to abide by the applicable terms of use and/or privacy policy for each feature employing AI.';
      if (system === 'era')
        return 'You acknowledge that the certain features provided by Reynolds as part of the ERA DMS System (the "Services") may incorporate artificial intelligence and machine learning technologies ("AI"), including large language models, to process and analyze information provided to it and to generate responses based on that information. Some responses are generated automatically and may be imprecise, incomplete, incorrect or out of date. You acknowledge that AI responses do not provide reliable professional, legal, financial, or other regulated advice, and any information or content provided by AI is for your general informational purposes only. You should not rely on any AI response as a substitute for professional advice, independent judgment, or independent verification. The Services may collect, process, and analyze the content of your inputs and the associated interaction data to operate, maintain, improve, and develop (1) Reynolds\' services; and (2) the underlying AI models. Unless necessary for the intended use of a feature of the Services, do not share confidential, proprietary, personal, or other sensitive information with the AI feature(s). You understand that your inputs and the AI outputs may be processed by third-party service providers and may be transferred, stored, or processed in other jurisdictions, which may have different data protection laws than your jurisdiction. By accessing the Services, you agree to abide by these terms, the applicable terms of use, and privacy policy for each feature of the Services that employs AI.';
      
      return null;
    },
    dealers() {
      return this.user_access_info?.dealers || [];
    },
    // Groups the caller can actually use, for display: user groups as-is; enterprise groups only
    // when they share at least one CIF with the caller's own dealers (the "access to ≥1 rooftop"
    // rule — display-only here, the server re-applies it at message time). accessibleCount is the
    // per-group overlap size shown as "Name (n)".
    // The rule itself is the store's usable_dealer_groups getter, shared with the morning brief's
    // Covers picker so the two cannot offer different groups.
    usable_groups() {
      return this.mainStore.usable_dealer_groups;
    },
    all_rooftops_group_id() {
      // Reserved virtual group id — the backend resolves it to every rooftop the caller can
      // access (DealerGroupConstants.AllRooftopsGroupId).
      return ALL_ROOFTOPS_GROUP_ID;
    },
    show_all_rooftops_option() {
      return this.dealers.length > 1;
    },
    show_groups_section() {
      return this.show_all_rooftops_option || this.usable_groups.length > 0;
    },
    active_dealer_label() {
      if (this.selectedGroupId === this.all_rooftops_group_id)
        return `${this.$t('messaging.input.allRooftops')} · ${this.dealers.length}`;
      if (this.selectedGroupId) {
        const group = this.usable_groups.find(g => g.id === this.selectedGroupId);
        if (group) return `${group.name} · ${group.accessibleCount}`;
      }
      const dealer = this.dealers.find(d => d.cifNumber === this.selectedDealerCif);
      return dealer?.name || dealer?.cifNumber || '';
    },
    /**
     * "Topic: {name}" for the composer, or empty when there is no topic to name.
     *
     * The prefix is localized rather than concatenated in the template: Spanish and French both
     * want their own word for it, and French puts a space before the colon.
     */
    topic_label() {
      const name = (this.topicName || '').trim();
      if (!name) return '';

      return this.$t('messaging.input.topicLabel', { name });
    },
    user_token() {
      return this.mainStore.token;
    },
    messenger_api_base_uri() {
      return this.mainStore.messenger_api_base_uri;
    },
    // Language tag sent with voice transcription. Prefers the Rey spoken-language preference and
    // falls back to the System Language, then English. Note this is the FULL tag ('es-MX'), not the
    // normalized catalog subtag the UI renders with — the transcription API expects a locale.
    transcription_language() {
      const flat = this.mainStore.user_settings?.flat;
      return flat?.['Ux.SpokenLanguage'] || flat?.['Ux.Language'] || 'en-US';
    },
    component_key() {
      return this.$attrs.key?this.$attrs.key:('messenger_input_'+Date.now());
    },
    component_id() {
      return this.component_key+'_container';
    },
    emoji_index() {
      return this.emojiIndexObj;// || emojiIndex
    },
    current_user_guid() {
      return this.user_access_info?.userId || null;
    },
    has_ask_rey_access() {
      return this.mainStore.has_ask_rey_access;
    },
    message_send_ready() {
      return this.messageSendReady && !this.processing_thread_reset;
    },
    enterprise_users() {
      return this.usersInfo?.enterpriseUsers || [];
    },
    ent_users_by_guid() {
      return this.$FUSarrayOfObjectsKeyMap(this.enterprise_users, 'personGuid') || {};
    },
    company_user_guids() {
      return this.usersInfo?.companyUsers || [];
    },
    company_users() {
      const self = this;
      return this.company_user_guids.map(x => self.ent_users_by_guid[x]).filter(x => x && x.messagingEnabled);
    },
    participant_guid_set() {
      // Person GUIDs of the current thread's live participant roster, used to scope the @ mention
      // list to people actually in the chat. Empty when no thread is open (e.g. AI/welcome input).
      return new Set(Array.isArray(this.participantGuids) ? this.participantGuids : []);
    },
    system_users_tagged() {
      if (!this.current_tagged_users || this.current_tagged_users.length === 0)
        return false;
      
      return this.current_tagged_users.some(userId => userId && userId.toString().startsWith(this.$FUSsystemUserPrefix));
    },
    non_system_users_tagged() {
      if (!this.current_tagged_users || this.current_tagged_users.length === 0)
        return false;
      
      return this.current_tagged_users.some(userId => userId && !userId.toString().startsWith(this.$FUSsystemUserPrefix));
    },
    user_tag_list() {
      const self = this;
      let fieldData = [];
      if (this.new_message_tag_key && Array.isArray(this.company_users)) {
        let userTag = this.new_message_tag_key.substring(1).toLowerCase(); // remove '@'
        return this.company_users.filter(user => {
          let userSearchText = `${user.email} ${user.friendlyName} ${user.fullName} ${user.userId}`;
          // Never offer yourself as a mention target.
          if (user.personGuid === self.current_user_guid) return false;
          // Only Rey (system_REY) is exempt from participant scoping — it stays mentionable when the
          // user has Ask-Rey access. Every other user (humans AND other system personas like Avery,
          // Alex, etc.) must be a participant of the current thread to appear in the @ mention list.
          const isReyAssistant = user.personGuid.indexOf(self.$FUSsystemUserPrefix + 'REY') === 0;
          const allowedInThread = isReyAssistant ? self.has_ask_rey_access : self.participant_guid_set.has(user.personGuid);
          return allowedInThread && userSearchText.toLowerCase().includes(userTag);
        }).sort((a, b) => {
          const aName = (a.friendlyName || '').toLowerCase();
          const bName = (b.friendlyName || '').toLowerCase();
          const aStarts = aName.startsWith(userTag);
          const bStarts = bName.startsWith(userTag);
          if (aStarts !== bStarts) return aStarts ? -1 : 1;
          return aName.localeCompare(bName);
        });
      }
      return fieldData;
    },
    show_tagged_users() {
      return this.can_show_tagged_users && this.new_message_tag_key && this.$FUSarrayHasEntries(this.user_tag_list);
    },
    new_message_tag_key() {
      if (this.isSystemAi) return null;
      let tagKey = '@';
      let messageFromToken = this.getSubstringUpToFirstSpaceLeft(this.new_message, this.new_message_cursor_position);
      let tagKeys = messageFromToken.split(tagKey).length - 1;
      if(messageFromToken.indexOf(tagKey)===0 && messageFromToken.indexOf(' ')<0 && tagKeys===1)
        return messageFromToken;
      
      return null;
    },
    input_placehoder() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('placeholder'))
        return this.inputConfig.placeholder;
      
      return '';
    },
    show_bottom_buttons() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('bottomButtons'))
        return this.inputConfig.bottomButtons;
      
      return false;
    },
    show_send1() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('send1'))
        return this.inputConfig.send1;
      
      return false;
    },
    show_send2() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('send2'))
        return this.inputConfig.send2;
      
      return false;
    },
    show_send3() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('send3'))
        return this.inputConfig.send3;
      
      return false;
    },
    show_tagging() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('tagging'))
        return this.inputConfig.tagging;
      
      return false;
    },
    show_file_upload1() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('fileUpload1'))
        return this.inputConfig.fileUpload1;
      
      return false;
    },
    show_file_upload2() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('fileUpload2'))
        return this.inputConfig.fileUpload2;
      
      return false;
    },
    show_file_upload3() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('fileUpload3'))
        return this.inputConfig.fileUpload3;
      
      return false;
    },
    show_emoji() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('emoji'))
        return this.inputConfig.emoji;
      
      return false;
    },
    show_trash() {
      if(this.inputConfig && this.inputConfig.hasOwnProperty('trash'))
        return this.inputConfig.trash;
      
      return false;
    },
  },
  methods: {
    openAiDisclosureModal() {
      this.show_ai_disclosure_modal = true;
    },
    selectDealer(cifNumber) {
      this.show_context_options = false;
      this.$emit('updateContext', cifNumber);
    },
    selectGroup(group) {
      this.show_context_options = false;
      this.$emit('updateGroupContext', { id: group.id, name: group.name });
    },
    selectAllRooftops() {
      this.show_context_options = false;
      this.$emit('updateGroupContext', { id: this.all_rooftops_group_id, name: this.$t('messaging.input.allRooftops') });
    },
    openGroupManager() {
      this.show_context_options = false;
      this.show_group_manager = true;
    },
    handleGroupDeleted(groupId) {
      // The active scope was just deleted — fall back to single-dealer scope.
      if (groupId && groupId === this.selectedGroupId)
        this.$emit('updateGroupContext', null);
    },
    fetchDealerGroups() {
      this.mainStore.fetchDealerGroups();
    },
    handleDealerListClickOutside(event) {
      if (!this.show_context_options) return;
      const popup = this.$refs.dealer_context_popup;
      const trigger = this.$refs.dealer_context_trigger;
      const isClickOnTrigger = trigger && (trigger === event.target || trigger.contains(event.target));
      if (popup && !popup.contains(event.target) && !isClickOnTrigger) {
        this.show_context_options = false;
      }
    },
    handleTyping(sendRequest=true) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const rangeStartContainer = range.startContainer;
      const rangeStartContainerText = rangeStartContainer.textContent;
      let parentElement = rangeStartContainer.nodeType===Node.TEXT_NODE?range.startContainer.parentElement:rangeStartContainer;
      this.new_message_cursor_position = range.startOffset;
      if(parentElement.matches("span.tagged-user") || range.startOffset!==range.endOffset)
        this.new_message = '';
      else
        this.new_message = rangeStartContainerText;
      
      if(sendRequest)
        this.$emit('userTyping');
      
      this.can_show_tagged_users = true;
      this.show_emoji_window = false;
      this.fixInvalidSelection();
    },
    handleKeyDownEvent(event) {
      switch (event.key) {
        case 'Shift':
          this.shift_down = true;
          break;
      }
    },
    handleKeyUpEvent(event) {
      switch (event.key) {
        case 'Shift':
          this.shift_down = false;
          break;
      }
    },
    handleBackspace(event) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      if(range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE && range.startOffset > 2) {
        const oneBack = range.commonAncestorContainer.childNodes[range.startOffset-1];
        const twoBack = range.commonAncestorContainer.childNodes[range.startOffset-2];
        const threeBack = range.commonAncestorContainer.childNodes[range.startOffset-3];
        if(oneBack?.nodeType === Node.ELEMENT_NODE && oneBack.nodeName === 'IMG' &&
          twoBack?.nodeType === Node.ELEMENT_NODE && twoBack.getAttribute('contenteditable') === 'false' &&
          threeBack?.nodeType === Node.ELEMENT_NODE && threeBack.nodeName === 'IMG') {
          const prevNode = threeBack.previousSibling;
          const nextNode = oneBack.nextSibling;
          oneBack.remove();
          twoBack.remove();
          threeBack.remove();
          event.preventDefault();
          if(prevNode) {
            const newRange = new Range();
            newRange.setStartAfter(prevNode);
            newRange.setEndAfter(prevNode);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
          else if(nextNode) {
            const newRange = new Range();
            newRange.setStartBefore(nextNode);
            newRange.setEndBefore(nextNode);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
          
          this.fixInvalidSelection();
        }
      }
    },
    escMessage(event=null) {
      if(event!==null) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.can_show_tagged_users = false;
      this.show_emoji_window = false;
    },
    handleArrowLeftRight(event) {
      const selection = window.getSelection();
      if (selection.rangeCount === 0)
        return;
      
      const range = selection.getRangeAt(0);
      const currentNode = range.commonAncestorContainer;
      
      if(event.key === 'ArrowRight') {
        if(currentNode.nodeType === Node.TEXT_NODE && currentNode.length < range.startOffset + 1 || currentNode.nodeType === Node.ELEMENT_NODE) {
          const nextNode = currentNode.nodeType === Node.TEXT_NODE ? currentNode.nextSibling : currentNode.childNodes[range.startOffset];
          if(nextNode && nextNode.nodeType === Node.ELEMENT_NODE && nextNode.nodeName === 'IMG') {
            event.preventDefault();
            const newRange = new Range();
            newRange.setStartAfter(nextNode.nextSibling.nextSibling);
            newRange.setEndAfter(nextNode.nextSibling.nextSibling);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      }
      else {
        if(currentNode.nodeType === Node.ELEMENT_NODE) {
          const prevNode = currentNode.childNodes[range.startOffset - 1];
          if(prevNode && prevNode.nodeType === Node.ELEMENT_NODE && prevNode.nodeName === 'IMG') {
            event.preventDefault();
            const newRange = new Range();
            newRange.setStartBefore(prevNode.previousSibling.previousSibling);
            newRange.setEndBefore(prevNode.previousSibling.previousSibling);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      }
    },
    handleSelectionChange(event) {
      const selection = document.getSelection();
      //if rangeCount is 0, no selection is made (usually means it's a freshly loaded page)
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const editableDiv = this.$refs.messenger_input;
        this.cursor_within_input = editableDiv && editableDiv.contains(range.commonAncestorContainer);
        if(this.cursor_within_input)
          this.active_range = range;
      }
      else {
        this.cursor_within_input = false;
      }
      
      if(!this.cursor_within_input) {
        this.shift_down = false;
        this.can_show_tagged_users = false;
      }
      else {
        this.handleTyping(false);
      }
    },
    selectActiveTaggedUser(event) {
      if(this.show_tagged_users) {
        event.stopPropagation();
        event.preventDefault();
        const taggedUser = this.user_tag_list[this.active_tagged_user];
        this.insertContentObject('USER_TAG', taggedUser);
      }
    },
    setActiveTaggedUser(event, next=true) {
      if(this.show_tagged_users) {
        event.stopPropagation();
        event.preventDefault();
        if (this.active_tagged_user === null)
          this.active_tagged_user = 0;
        else {
          if (next)
            this.active_tagged_user = (this.active_tagged_user + 1) % this.user_tag_list.length;
          else
            this.active_tagged_user = (this.active_tagged_user - 1 + this.user_tag_list.length) % this.user_tag_list.length;
        }
        this.scrollActiveTaggedUser();
      }
    },
    setTaggedUsers() {
      const messengerInput = this.$refs.messenger_input;
      if(messengerInput) {
        const taggedUsers = messengerInput.querySelectorAll('span.tagged-user[data-user-id]');
        this.current_tagged_users = Array.from(taggedUsers).map(span => span.getAttribute('data-user-id'));
      }
    },
    // ── Rey composer attachments ──────────────────────────────────────────────────────────────
    // Staged in the browser, uploaded on send. Kept separate from openAttachment/filePondProcess
    // below: those are still referenced by the other modes' disabled buttons, and repointing them
    // would silently change what those do if anyone ever re-enables them.

    // The <label for> opens the dialog by itself — no JavaScript involved, which is the point. This
    // only clears the previous error and blocks the gesture when the composer is not ready.
    //
    // Replaced an @click that called input.click() through $refs. That path silently did nothing for
    // the user while drag-and-drop worked, even though BOTH guard on exactly the same two conditions
    // — so the guard was never the problem and the failure was in the opening itself: either the ref
    // or a display:none input, which is the shape browsers are fussiest about. Rather than guess
    // which, the native pairing removes both possibilities.
    onAttachmentTriggerClick(event) {
      if (!this.message_send_ready || !this.attachments_enabled) {
        if (event) event.preventDefault();
        return;
      }
      this.stage_error = '';
      this.stage_notice = '';
    },

    onAttachmentInputChange(event) {
      const input = event && event.target;
      this.stageFiles(input && input.files);
      // Reset so picking the same file twice in a row still fires change.
      if (input) input.value = '';
    },

    /** Per file: two staged documents can go to different places in the same message. */
    toggleShare(stagedId) {
      const staged = this.staged_files.find(file => file.id === stagedId);
      if (staged) staged.share_with_dealership = !staged.share_with_dealership;
    },

    shareToggleTitle(staged) {
      return staged.share_with_dealership
        ? this.$t('messaging.input.shareOn', { name: staged.name })
        : this.$t('messaging.input.shareOff', { name: staged.name });
    },

    onComposerDragEnter(event) {
      if (!this.attachments_enabled || !this.message_send_ready) return;
      if (!this.eventCarriesFiles(event)) return;
      this.drop_depth += 1;
      this.drop_active = true;
    },

    onComposerDragLeave() {
      if (!this.drop_active) return;
      // Depth-counted: dragging across child elements fires leave/enter pairs, and decrementing
      // rather than clearing stops the overlay flickering as the pointer crosses the chip row.
      this.drop_depth = Math.max(0, this.drop_depth - 1);
      if (this.drop_depth === 0) this.drop_active = false;
    },

    onComposerDrop(event) {
      this.drop_depth = 0;
      this.drop_active = false;
      if (!this.attachments_enabled || !this.message_send_ready) return;
      this.stageFiles(event && event.dataTransfer && event.dataTransfer.files);
    },

    eventCarriesFiles(event) {
      const types = event && event.dataTransfer && event.dataTransfer.types;
      if (!types) return false;
      return Array.prototype.indexOf.call(types, 'Files') >= 0;
    },

    /**
     * Asks the server whether this store may attach files. Called on mount and whenever the store in
     * context changes, and only for Rey's composer — the other modes' attachment buttons stay behind
     * their existing v-if="false" and asking on their behalf would be a request nothing reads.
     */
    refreshAttachmentAvailability() {
      // Gated on WHERE the picker could appear, never on attachments_enabled: that now includes the
      // answer this call fetches, so asking it here would mean never asking the server and the picker
      // staying hidden for good.
      if (!this.attachments_in_reys_composer || !this.current_cif) return;
      this.mainStore.LoadAttachmentAvailability(this.current_cif);
    },

    stageFiles(fileList) {
      const files = fileList ? Array.from(fileList) : [];
      if (!files.length) return;

      // Clear both here as well as on the + button: drag-and-drop and paste reach this directly, and a
      // notice left over from the previous attachment would read as being about this one.
      this.stage_error = '';
      this.stage_notice = '';

      // Refused here rather than at send. The upload endpoint refuses too — it is the authority and this
      // cannot replace it — but it only answers once the user has staged a file, typed a question and
      // pressed send, and the work is then lost. Same sentence, from the server, at the moment of the
      // gesture. Nothing is staged, so there is nothing to clean up.
      const denied = this.attachments_denied_reason;
      if (denied) {
        this.stage_error = denied;
        return;
      }

      const accepted = this.accepted_attachment_extensions.split(',');
      const rejected = [];
      const legacy = [];
      const tooLarge = [];
      let overImageLimit = 0;

      files.forEach(file => {
        const name = file.name || '';
        const dot = name.lastIndexOf('.');
        const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';

        // Legacy Office gets its own message naming the modern equivalent. Lumping it in with "can't
        // read this" would leave the user with no idea that saving as .docx is all that is needed.
        const modern = this.legacy_office_extensions[ext];
        if (modern) {
          legacy.push(`${name} (${this.$t('messaging.input.saveAsModern', { format: modern })})`);
          return;
        }

        if (accepted.indexOf(ext) < 0) {
          rejected.push(name);
          return;
        }

        const isMedia = this.media_attachment_extensions.indexOf(ext) >= 0;
        const cap = isMedia ? this.max_media_bytes : this.max_document_bytes;
        if ((file.size || 0) > cap) {
          tooLarge.push(`${name} (${this.$t('messaging.input.sizeOverLimit', { size: this.formatBytes(file.size), cap: this.formatBytes(cap) })})`);
          return;
        }

        const isImage = this.image_attachment_extensions.indexOf(ext) >= 0;
        // The server keeps only the first four images per turn and drops the rest without a word.
        // Refusing here means the limit is visible when it applies, instead of the user believing
        // Rey read something it never saw.
        if (isImage && this.staged_image_count >= this.max_staged_images) {
          overImageLimit += 1;
          return;
        }

        this.staged_file_seq += 1;
        const staged = {
          id: `staged-${this.staged_file_seq}`,
          name,
          type: file.type || '',
          size: file.size || 0,
          is_image: isImage,
          is_media: isMedia,
          // Per file, and off by default. Putting a document into the store's shared corpus is not the
          // safe assumption — every user of that rooftop can then retrieve it — so it takes a click.
          share_with_dealership: false,
          // '' | 'uploading' | 'sent' | 'failed'. Drives the chip's own progress line; see uploadStagedFiles.
          upload_state: '',
          url: isImage ? URL.createObjectURL(file) : '',
          file,
          // Derived uploads for media: the speech as 16 kHz WAV plus sampled frames. Filled in by
          // extractMediaFor below; until then the chip shows its progress.
          derived: null,
          extract_state: isMedia ? 'pending' : '',
          extract_label: '',
        };
        this.staged_files.push(staged);

        // Extraction starts NOW, not at send. Decoding a long recording takes seconds, and doing it when
        // the user presses send would add that to the wait for their answer — whereas doing it while they
        // type is free. The upload still happens only on send.
        if (isMedia) this.extractMediaFor(staged.id);
      });

      const problems = [];
      if (legacy.length) problems.push(legacy.join('; '));
      if (rejected.length) problems.push(this.$t('messaging.input.cantRead', { names: rejected.join(', ') }));
      if (tooLarge.length) problems.push(this.$t('messaging.input.tooLarge', { files: tooLarge.join('; ') }));
      if (overImageLimit) problems.push(this.$t('messaging.input.imageLimit', { count: this.max_staged_images }));
      this.stage_error = problems.join('. ');
    },

    // Replaces a staged media file with what Rey can actually read: its speech as 16 kHz WAV and a few
    // frames. The source is NOT uploaded — that is the point. A 50 MB video was refused outright by the
    // transcription endpoint ("Maximum file size exceeded"), and its speech is a fraction of that size.
    async extractMediaFor(stagedId) {
      const staged = this.staged_files.find(f => f.id === stagedId);
      if (!staged || !staged.file) return;

      staged.extract_state = 'working';
      try {
        const { files, notes } = await extractMedia(staged.file, {
          onProgress: label => { staged.extract_label = label; },
        });

        await this.applyExtractionResult(stagedId, { files, notes });
      } catch (e) {
        if (this.staged_files.some(f => f.id === stagedId)) staged.extract_state = 'failed';
        this.stage_error = this.$t('messaging.input.couldNotRead', { name: staged.name });
      }
    },

    /**
     * Folds an extraction result into the chip. Separate from extractMediaFor so the decisions here can
     * be tested without a media pipeline — decodeAudioData and video seeking do not exist under jsdom,
     * and a test that mocks them only proves the mocks agree with each other.
     */
    async applyExtractionResult(stagedId, { files, notes }) {
      const staged = this.staged_files.find(f => f.id === stagedId);
      // The chip may have been removed while we were decoding.
      if (!staged) return;

      if (files.length > 0) {
        staged.derived = files;
        staged.extract_state = 'ready';
        // Free the source: keeping a 1.4 GB File referenced after deriving a few MB from it is the
        // whole problem this avoids.
        staged.file = null;
      } else {
        staged.extract_state = 'failed';
      }

      // The same note means different things depending on whether anything came out. With derived
      // files, it is a caveat about what was produced ("split into 2 parts"); with none, it is the
      // explanation of why nothing was. Only the second is a failure.
      if (notes.length) {
        if (files.length > 0) this.stage_notice = notes.join(' ');
        else this.stage_error = notes.join(' ');
      }

      await this.$nextTick();
    },

    formatBytes(bytes) {
      const mb = (Number(bytes) || 0) / (1024 * 1024);
      return mb >= 1 ? `${mb.toFixed(mb >= 10 ? 0 : 1)} MB` : `${Math.max(1, Math.round((Number(bytes) || 0) / 1024))} KB`;
    },

    removeStagedFile(id) {
      const index = this.staged_files.findIndex(f => f.id === id);
      if (index < 0) return;
      const staged = this.staged_files[index];
      // Release the preview blob; the browser keeps it alive for the document otherwise.
      if (staged.url) {
        try { URL.revokeObjectURL(staged.url); } catch (e) { /* already revoked */ }
      }
      if (staged.abort) {
        try { staged.abort(); } catch (e) { /* request already settled */ }
      }
      this.staged_files.splice(index, 1);
      this.stage_error = '';
      this.stage_notice = '';
    },

    clearStagedFiles() {
      this.staged_files.forEach(staged => {
        if (staged.url) {
          try { URL.revokeObjectURL(staged.url); } catch (e) { /* already revoked */ }
        }
      });
      this.staged_files = [];
      this.stage_error = '';
      this.stage_notice = '';
    },

    // One upload per file to send. Media expands into several — the WAV and each frame — because the
    // server ingests one file per request and each derived piece takes a different lane there (transcript
    // versus image).
    filesToUploadFor(staged) {
      if (staged.derived && staged.derived.length) return staged.derived;
      return staged.file ? [staged.file] : [];
    },

    async uploadStagedFile(staged) {
      const files = this.filesToUploadFor(staged);
      if (files.length === 0) throw new Error(`nothing to upload for ${staged.name}`);

      for (let i = 0; i < files.length; i += 1) {
        const formData = new FormData();
        formData.append('File', files[i]);
        formData.append('ThreadId', this.threadId);
        formData.append('UserId', this.current_user_guid);
        // Rides along with the upload: the answer was given before the file left the browser, so the
        // server files it in the right scope the first time. The store is named rather than implied,
        // and the server validates it against this user's own associations.
        if (staged.share_with_dealership && this.share_on_upload_enabled) {
          formData.append('ShareWithDealershipCif', this.current_cif);
        }
        const { promise, abort } = this.uploadFileViaStore(formData);
        staged.abort = abort;
        await promise;
      }
    },

    // Store actions are always reached through a local wrapper, per the project convention.
    uploadFileViaStore(formData) {
      return this.mainStore.UploadFile(formData);
    },

    // Sequential on purpose: the server AWAITS ingestion (parse, chunk, embed) inside the upload
    // call, so firing these in parallel would stack concurrent ingests without shortening the
    // critical path. Returns the names that failed.
    async uploadStagedFiles() {
      const failed = [];
      const queued = this.staged_files.slice();
      for (let i = 0; i < queued.length; i += 1) {
        const staged = queued[i];
        // Marked on the chip itself, not just via the composer-wide spinner. The server awaits parse,
        // chunk and embed inside the upload call, so a document takes around three seconds — and the
        // only sign of it was a small loader replacing the paperclip, away from the chips, while the
        // typed message sat in the box looking unsent. Naming which file is going, one at a time,
        // is what makes that pause read as progress rather than as nothing happening.
        staged.upload_state = 'uploading';
        try {
          await this.uploadStagedFile(staged);
          staged.upload_state = 'sent';
        } catch (e) {
          staged.upload_state = 'failed';
          failed.push(staged.name);
        }
      }
      return failed;
    },

    async sendWithStagedFiles(newMessage) {
      this.uploading_file = true;
      const queuedCount = this.staged_files.length;   // read before clearStagedFiles empties the list
      let failed = [];
      try {
        failed = await this.uploadStagedFiles();
      } finally {
        this.uploading_file = false;
      }

      this.clearStagedFiles();

      // Tells the promote prompt to re-read the thread's documents. Fired even when some uploads failed,
      // because the ones that succeeded are promotable and the prompt filters on what the server reports
      // rather than on what this method believes it sent.
      if (queuedCount > 0) window.event_bus.$emit('ATTACHMENT-UPLOADED');

      // The message goes out either way — being unable to attach must not trap a typed question.
      // The note is what keeps the answer honest: without it, Rey replies with no document and the
      // user has no way to know why.
      if (failed.length) {
        this.stage_error = this.$t('messaging.input.attachmentNotDelivered', { names: failed.join(', ') });
      }

      this.$emit('commitInput', newMessage);
    },

    openAttachment() {
      if(this.message_send_ready)
        this.$refs.messenger_upload_file_pond.browse();
    },
    showUserTagging() {
      if (!this.message_send_ready) return;

      // If an @ tag key is already active, just close the list
      if (this.new_message_tag_key) {
        this.can_show_tagged_users = false;
        return;
      }

      const input = this.$refs.messenger_input;
      if (!input) return;

      input.focus();

      // Restore cursor to last known position so @ inserts in the right place
      const selection = window.getSelection();
      if (this.active_range) {
        selection.removeAllRanges();
        selection.addRange(this.active_range);
      } else {
        // No saved range — place cursor at end of input
        this.ensureParagraphTag();
        const paragraphs = input.querySelectorAll('p');
        const lastParagraph = paragraphs[paragraphs.length - 1];
        if (lastParagraph) {
          const newRange = document.createRange();
          newRange.selectNodeContents(lastParagraph);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }

      this.$nextTick(() => {
        document.execCommand('insertText', false, '@');
        this.handleTyping(false);
      });
    },
    extractRawMessage(content) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      const taggedUsers = tempDiv.querySelectorAll('.tagged-user');
      const emojis = tempDiv.querySelectorAll('.emoji-text');
      const brs = tempDiv.querySelectorAll('br');
      const pTags = tempDiv.querySelectorAll('p');
      taggedUsers.forEach((tag) => {
        const userId = tag.dataset.userId;
        tag.replaceWith(` @${userId} `);
      });
      emojis.forEach((tag) => {
        const colons = tag.dataset.colons;
        tag.replaceWith(` ${colons} `);
      });
      brs.forEach((tag) => {
        tag.replaceWith(`_br_`);
      });
      pTags.forEach((tag, idx, arr) => {
        const text = tag.innerText.trim();
        if(text!=='_br_' && idx<(arr.length-1))
          tag.replaceWith(`${text}_br_`);
      });
      
      return tempDiv.innerText; // Return the raw message text
    },
    sendMessage(sendBtn=false) {
      const self = this;
      if(!sendBtn) {
        if(this.show_tagged_users) {
          const taggedUser = this.user_tag_list[this.active_tagged_user];
          this.insertContentObject('USER_TAG', taggedUser)
          return;
        }
        if(this.shift_down) {
          this.insertContentObject('LINE_BREAK');
          return;
        }
      }
      if(!this.message_send_ready)
        return;
      
      const content = this.$refs.messenger_input;
      let newMessage = this.extractRawMessage(content.innerHTML).trim();
      if (!newMessage || newMessage === '_br_') return;

      // Staged attachments go up BEFORE the message, so the document is ingested and therefore
      // resolvable by the time the orchestrator handles the turn.
      if (this.attachments_enabled && this.$FUSarrayHasEntries(this.staged_files)) {
        // Sending mid-extraction would upload nothing for that file and leave Rey answering about a
        // recording it never received. Waiting is the honest behaviour; the message is not lost.
        if (this.extraction_in_flight) {
          this.stage_error = this.$t('messaging.input.stillReading');
          return;
        }
        this.sendWithStagedFiles(newMessage);
      } else {
        this.$emit('commitInput', newMessage);
      }
      
      setTimeout(() => {
        if(self.$refs.messenger_input) {
          self.$refs.messenger_input.innerHTML = '';
          self.new_message = '';
          self.setTaggedUsers();
          self.ensureParagraphTag();
          self.$nextTick(() => {
            const inputEl = self.$refs.messenger_input;
            const newP = inputEl.querySelector("p");
            if (newP) {
              const range = document.createRange();
              const selection = window.getSelection();
              
              range.setStart(newP, 0);
              range.collapse(true);
              
              selection.removeAllRanges();
              selection.addRange(range);
            }
          });
        }
      }, 100);
      this.typing_already_sent = false;
    },
    ensureParagraphTag() {
      const editableDiv = this.$refs.messenger_input;
      if(editableDiv) {
        let initialized = false;
        if (![...editableDiv.childNodes].some(lineNode => lineNode.nodeType === Node.ELEMENT_NODE && lineNode.nodeName === 'P')) {
          // Wrap the content in a <p> tag
          const p = document.createElement('p');
          p.className = 'mb-0 message-input-line';
          while(editableDiv.firstChild) {
            p.appendChild(editableDiv.removeChild(editableDiv.firstChild));
          }
          editableDiv.appendChild(p);
          initialized = true;
        }
        
        // fix nodes that escaped out of the paragraph tag(s)
        let currentParagraph = null;
        [...editableDiv.childNodes].forEach(node => {
          if(node.nodeType !== Node.ELEMENT_NODE || node.nodeName !== 'P') {
            if(currentParagraph === null) {
              currentParagraph = document.createElement('p');
              currentParagraph.className = 'mb-0 message-input-line';
              editableDiv.insertBefore(currentParagraph, node.nextSibling);
            }
            currentParagraph.appendChild(editableDiv.removeChild(node));
          }
          else
            currentParagraph = null;
        });
        
        [...editableDiv.childNodes].forEach(lineNode => {
          let hasContent = false;
          let hasBr = false;
          [...lineNode.childNodes].forEach(node => {
            if(node.nodeType === Node.ELEMENT_NODE && node.getAttribute('contenteditable') === 'false' || node.nodeType === Node.TEXT_NODE && node.textContent !== '')
              hasContent = true;
            else if(node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'BR')
              hasBr = true;
            else if(node.nodeType === Node.TEXT_NODE && node.textContent === '')
              node.remove();
            else if(node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'SPAN' && node.getAttribute('contenteditable') !== 'false') {
              if(node.classList.contains('tagged-user') || node.classList.contains('emoji')) {
                // fix contenteditable=false being removed from span in mobile safari when a line is backspaced up to the prior line
                node.setAttribute('contenteditable', 'false');
                hasContent = true;
              }
              else {
                // remove extraneous spans added by browser
                const nextNode = node.nextSibling;
                lineNode.removeChild(node);
                while(node.firstChild)
                  lineNode.insertBefore(node.removeChild(node.firstChild), nextNode);
              }
            }
          });
          if(!hasContent && !hasBr)
            lineNode.appendChild(document.createElement('br'));
          else if(hasContent && hasBr)
            [...lineNode.childNodes].forEach(node => {if(node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'BR') node.remove()});
        });
        if(initialized) {
          const selection = window.getSelection();
          const newRange = new Range();
          newRange.selectNodeContents(editableDiv.firstChild);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        } 
      }
    },
    handleInputFocus() {
      if(this.message_send_ready) {
        this.ensureParagraphTag();
        this.$emit('inputFocus');
      }
    },
    insertContentObject(insertType, insertInfo=null) {
      switch (insertType) {
        case 'USER_TAG':
          this.insertUserInfo(insertInfo);
          break;
        case 'EMOJI':
          this.insertEmoji(insertInfo);
          break;
        case 'LINE_BREAK':
          this.insertLineBreak();
          break;
        default:
          return;
      }
      
      if(insertInfo && this.system_users_tagged && insertInfo.personGuid.indexOf(this.$FUSsystemUserPrefix)>=0 || this.non_system_users_tagged && insertInfo.personGuid.indexOf(this.$FUSsystemUserPrefix)<0)
        this.can_show_tagged_users = false;
      this.show_emoji_window = false;
    },
    insertUserInfo(userInfo) {
      if(this.system_users_tagged && userInfo.personGuid.indexOf(this.$FUSsystemUserPrefix)<0 || this.non_system_users_tagged && userInfo.personGuid.indexOf(this.$FUSsystemUserPrefix)>=0)
        return;
      
      const displayName = userInfo?.friendlyName || "Unknown";
      const userId = userInfo?.personGuid || null;
      const contentObj = document.createElement("span");
      contentObj.className = 'tagged-user font-bold';
      contentObj.contentEditable = "false";
      // FUS-474 (Security Backlog L6): the name is a text node, never markup — a display name is user-set.
      contentObj.innerHTML = '<i class="ri-at-line pr-1" style="vertical-align: middle;"></i>';
      contentObj.appendChild(document.createTextNode(displayName));
      contentObj.dataset.userId = userId;
      
      this.replaceContentAtCursor(contentObj);
      this.setTaggedUsers();
    },
    insertLineBreak() {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      range.deleteContents();
      
      // Find the closest <p> tag that contains the cursor
      let currentP = range.startContainer;
      while (currentP && currentP.nodeName !== "P") {
        currentP = currentP.parentElement;
      }
      //TODO: Maybe we need to wrap the message with a p tag if this is true? 
      if (!currentP) return;
      
      // Clone the range to preserve its contents
      const beforeRange = range.cloneRange();
      const afterRange = range.cloneRange();
      
      // Move `beforeRange` to cover everything before the cursor
      beforeRange.setStart(currentP, 0);
      beforeRange.setEnd(range.startContainer, range.startOffset);
      
      // Move `afterRange` to cover everything after the cursor
      afterRange.setStart(range.startContainer, range.startOffset);
      afterRange.setEnd(currentP, currentP.childNodes.length);
      
      // Extract the contents while keeping all nested HTML tags intact
      const beforeContent = beforeRange.extractContents();
      const afterContent = afterRange.extractContents();
      
      if(afterContent.childNodes.length === 0 || afterContent.firstChild.nodeType === Node.TEXT_NODE && afterContent.firstChild.textContent === '')
        afterContent.appendChild(document.createElement('br'))
      
      // Create a new <p> tag for the second part
      const newP = document.createElement("p");
      newP.className = "mb-0 message-input-line";
      newP.appendChild(afterContent);
      
      // Handle cases where the cursor is inside nested inline tags
      this.handleNestedInlineTags(range, currentP, beforeContent, newP);
      
      // Clear the current <p> and insert only the "before" part
      currentP.innerHTML = ""; // Remove old content
      currentP.appendChild(beforeContent); // Insert only the preserved before-content
      
      // Insert the new <p> tag as a sibling after the current <p>
      currentP.after(newP);
      
      // Move the cursor to the start of the new <p>
      const newRange = document.createRange();
      newRange.setStart(newP, 0);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
      this.active_range = newRange;
    },
    handleNestedInlineTags(range, currentP, beforeContent, newP) {
      let startNode = range.startContainer;
      if (startNode.nodeType === Node.TEXT_NODE && startNode.parentElement !== currentP) {
        let inlineParent = startNode.parentElement;
        
        // Recursively move up through nested inline elements
        const stack = [];
        while (inlineParent && inlineParent !== currentP) {
          stack.push(inlineParent);
          inlineParent = inlineParent.parentElement;
        }
        
        // Process the deepest nested tag first
        let currentInline = startNode;
        let inlineBeforeText = startNode.textContent.slice(0, range.startOffset);
        let inlineAfterText = startNode.textContent.slice(range.startOffset);
        
        if (inlineBeforeText.length > 0) {
          // Reconstruct the before part inside the same inline hierarchy
          let beforeInlineContainer = document.createDocumentFragment();
          stack.reverse().forEach((tag) => {
            let clonedTag = tag.cloneNode(false); // Clone only the structure
            beforeInlineContainer.appendChild(clonedTag);
            currentInline = clonedTag;
          });
          currentInline.textContent = inlineBeforeText;
          beforeContent.appendChild(beforeInlineContainer);
        }
        
        if (inlineAfterText.length > 0) {
          // Reconstruct the after part inside the new <p>
          let afterInlineContainer = document.createDocumentFragment();
          stack.reverse().forEach((tag) => {
            let clonedTag = tag.cloneNode(false);
            afterInlineContainer.appendChild(clonedTag);
            currentInline = clonedTag;
          });
          currentInline.textContent = inlineAfterText;
          newP.prepend(afterInlineContainer);
        }
      }
    },
    insertEmoji(emoji) {
      const contentObj = document.createElement("span");
      contentObj.className = 'emoji-text emoji';
      contentObj.contentEditable = "false";
      contentObj.dataset.emojiId = emoji.id;
      contentObj.dataset.colons = emoji.colons;
      contentObj.style = `background-position: ${emoji.getPosition()}`;
      this.insertContentAtCursor(contentObj);
    },
    replaceContentAtCursor(contentObj) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const parentNode = range.startContainer;
      
      if (parentNode.nodeType !== Node.TEXT_NODE) return; // Ensure it's a text node
      
      const text = parentNode.nodeValue; // Get the full text inside the node
      const cursorPosition = range.startOffset;
      
      // Find the last space before the cursor
      const substring = text.slice(0, cursorPosition);
      const match = substring.match(/\s(?!.*\s)/);
      const lastSpaceIndex = match ? substring.lastIndexOf(match[0]) : -1;
      
      // Get the mention word
      const mentionStart = lastSpaceIndex + 1; // Start of mention (after the last space)
      const mentionText = text.substring(mentionStart, cursorPosition);
      
      if (!mentionText.startsWith("@")) return; // Only process if it's a mention
      
      // Replace the mention text with the new contentObj
      range.setStart(parentNode, mentionStart);
      range.setEnd(parentNode, cursorPosition);
      range.deleteContents(); // Remove the original text
      
      this.insertBetweenBufferElements(contentObj, range);
      
      selection.removeAllRanges();
      selection.addRange(range);
    },
    insertBetweenBufferElements(contentObj, range) {
      const leftBufferElement = document.createElement('img');
      const rightBufferElement = document.createElement('img');

      // Make buffer elements invisible inline anchors (no broken-image rendering)
      [leftBufferElement, rightBufferElement].forEach(el => {
        el.style.cssText = 'width:0;height:0;border:0;margin:0;padding:0;display:inline;vertical-align:baseline;';
        el.setAttribute('aria-hidden', 'true');
      });
      
      range.insertNode(leftBufferElement);
      range.setStartAfter(leftBufferElement);
      range.setEndAfter(leftBufferElement);
      range.deleteContents(); // deleting here because it seems to add a text node
      
      range.insertNode(contentObj);
      range.setStartAfter(contentObj);
      range.setEndAfter(contentObj);
      range.deleteContents(); // deleting here because it seems to add a text node
      
      range.insertNode(rightBufferElement);
      range.setStartAfter(rightBufferElement);
      range.setEndAfter(rightBufferElement);
      range.deleteContents(); // deleting here because it seems to add a text node
    },
    insertContentAtCursor(contentObj) {
      const messengerInput = this.$refs.messenger_input;
      const selection = window.getSelection();
      selection.removeAllRanges();
      if(this.active_range) {
        // Restore the saved range
        selection.addRange(this.active_range);
      }
      else {
        this.ensureParagraphTag();
        const paragraphs = messengerInput.querySelectorAll('p');
        const lastParagraph = paragraphs[paragraphs.length - 1];
        
        if (lastParagraph) {
          const newRange = document.createRange();
          newRange.selectNodeContents(lastParagraph);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
      
      const range = selection.getRangeAt(0);
      const rangeCopy = range.cloneRange();
      range.deleteContents();
      this.mergeLines(rangeCopy.startContainer, rangeCopy.endContainer);
      range.setStart(rangeCopy.startContainer, rangeCopy.startOffset);
      range.setEnd(rangeCopy.startContainer, rangeCopy.startOffset);
      
      // Find all <br> tags within the startContainer
      if (range.startContainer.nodeType === Node.ELEMENT_NODE && range.startContainer.textContent==='') {
        const brTags = range.startContainer.querySelectorAll('br');
        brTags.forEach((br) => {
          br.remove();
        });
      }
      
      if(contentObj.nodeType === Node.ELEMENT_NODE)
        this.insertBetweenBufferElements(contentObj, range);
      else {
        range.insertNode(contentObj);
        range.setStartAfter(contentObj);
        range.setEndAfter(contentObj);
        range.deleteContents(); // deleting here because it seems to add a text node
      }
      
      selection.removeAllRanges();
      selection.addRange(range);
    },
    handlePaste(e) {
      const text = e.clipboardData ? e.clipboardData.getData('text') : '';

      // A pasted screenshot arrives on the clipboard as a FILE, and carries no text — so this handler
      // read an empty string and did nothing at all: no chip, no error, no clue the paste was ignored.
      // Ctrl+V of a screenshot is how people attach one, so it goes through the same staging path as
      // drag-and-drop and gets the same validation, chip and extraction.
      //
      // Guarded on there being no text rather than simply on files being present: copying a selection
      // from Excel or Word puts an image on the clipboard ALONGSIDE the text, and taking the file in
      // that case would silently swallow the text the user meant to paste.
      const pastedFiles = e.clipboardData && e.clipboardData.files;
      if (!text && pastedFiles && pastedFiles.length
          && this.attachments_enabled && this.message_send_ready) {
        this.stageFiles(pastedFiles);
        return;
      }

      const lines = text.split(/\r?\n/);
      lines.forEach((line, index) => {
        if(index > 0)
          this.insertLineBreak();
        this.insertContentAtCursor(document.createTextNode(line));
      });
    },
    toggleEmojis() {
      if(this.message_send_ready) {
        this.fixInvalidSelection();
        // const selection = window.getSelection();
        this.activate_emoji_window = true;
        this.show_emoji_window = !Boolean(this.show_emoji_window);
        this.can_show_tagged_users = false
      }
    },
    fixInvalidCaret(range, isStart) {
      const currentNode = isStart ? range.startContainer : range.endContainer;
      const offset = isStart ? range.startOffset : range.endOffset;
      const leftNode = currentNode.nodeType === Node.ELEMENT_NODE ? currentNode.childNodes[offset-1] : currentNode.previousSibling;
      const rightNode = currentNode.nodeType === Node.ELEMENT_NODE ? currentNode.childNodes[offset] : currentNode.nextSibling;
      
      if(leftNode?.nodeType === Node.ELEMENT_NODE && leftNode.nodeName === 'IMG' &&
        rightNode?.nodeType === Node.ELEMENT_NODE && rightNode.getAttribute('contenteditable') === 'false') {
        if(isStart)
          range.setStartBefore(leftNode);
        else
          range.setEndBefore(leftNode);
        return true;
      }
      else if(rightNode?.nodeType === Node.ELEMENT_NODE && rightNode.nodeName === 'IMG' &&
        leftNode?.nodeType === Node.ELEMENT_NODE && leftNode.getAttribute('contenteditable') === 'false') {
        if(isStart)
          range.setStartAfter(rightNode);
        else
          range.setEndAfter(rightNode);
        return true;
      }
      else if(currentNode.nodeType === Node.ELEMENT_NODE && currentNode.getAttribute('contenteditable') === 'false') {
        const targetNode = currentNode.nextSibling || currentNode;
        if(isStart)
          range.setStartAfter(targetNode);
        else
          range.setEndAfter(targetNode);
        return true;
      }
      else if(currentNode.parentNode?.nodeType === Node.ELEMENT_NODE && currentNode.parentNode.getAttribute('contenteditable') === 'false') {
        const targetNode = currentNode.parentNode.nextSibling || currentNode.parentNode;
        if(isStart)
          range.setStartAfter(targetNode);
        else
          range.setEndAfter(targetNode);
        return true;
      }
      else if(currentNode.nodeType === Node.ELEMENT_NODE && currentNode.getAttribute('contenteditable') === 'true' ||
        currentNode.nodeType === Node.TEXT_NODE && currentNode.parentNode?.nodeType === Node.ELEMENT_NODE && currentNode.parentNode.getAttribute('contenteditable') === 'true') {
        if(rightNode?.nodeType === Node.ELEMENT_NODE && rightNode.classList.contains('message-input-line')) {
          if(isStart)
            range.setStart(rightNode, 0);
          else
            range.setEnd(rightNode, 0);
        }
        else if(leftNode?.nodeType === Node.ELEMENT_NODE && leftNode.classList.contains('message-input-line')) {
          if(isStart)
            range.setStart(leftNode, leftNode.childNodes.length);
          else
            range.setEnd(leftNode, leftNode.childNodes.length);
        }
        return true;
      }
      return false;
    },
    fixInvalidSelection() {
      if(this.cursor_within_input && this.message_send_ready) {
        this.ensureParagraphTag();
        const selection = window.getSelection();
        if(selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0).cloneRange();
        const fixedEnd = this.fixInvalidCaret(range, false);
        const fixedStart = this.fixInvalidCaret(range, true);
        if(fixedEnd || fixedStart) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    },
    getSubstringUpToFirstSpaceLeft(str, x) {
      if(x>0 && str && str.length>=x) {
        let subStr = str.substring(0, x);
        
        // Find the last space to the left of position x
        const substring = str.slice(0, x);
        const match = substring.match(/\s(?!.*\s)/);
        const lastSpaceIndex = match ? substring.lastIndexOf(match[0]) : -1;
        
        // If no space is found, return the substring up to x
        if (lastSpaceIndex === -1) {
          return subStr.trim();
        }
        // Otherwise, return the substring up to the last space
        return subStr.substring(lastSpaceIndex).trim();
      }
      
      return '';
    },
    scrollActiveTaggedUser() {
      let index = this.active_tagged_user || 0;
      this.$nextTick(() => {
        const links = this.$refs.user_tagging_suggest_links; // Array of <a> elements
        
        if (links && links[index]) {
          const target = links[index]; // The specific <a> tag
          target.scrollIntoView({ block: "center" });
        }
      });
    },
    mergeLines(startNode, endNode) {
      if(startNode.nodeType === Node.TEXT_NODE)
        startNode = startNode.parentNode;
      if(endNode.nodeType === Node.TEXT_NODE)
        endNode = endNode.parentNode;
      if(startNode === endNode)
        return;
      
      let currentNode = startNode.nextSibling;
      const stopNode = endNode.nextSibling;
      while(currentNode && currentNode !== stopNode) {
        while(currentNode.firstChild)
          startNode.appendChild(currentNode.removeChild(currentNode.firstChild));
        const nextNode = currentNode.nextSibling;
        currentNode.remove();
        currentNode = nextNode;
      }
    },
    replyToMessage(userInfo) {
      const displayName = userInfo?.friendlyName || "Unknown";
      const userId = userInfo?.personGuid || null;
      
      // Create the tagged user content object
      const contentObj = document.createElement("span");
      contentObj.className = 'tagged-user font-bold';
      contentObj.contentEditable = "false";
      // FUS-474 (Security Backlog L6): the name is a text node, never markup — a display name is user-set.
      contentObj.innerHTML = '<i class="ri-at-line pr-1" style="vertical-align: middle;"></i>';
      contentObj.appendChild(document.createTextNode(displayName));
      contentObj.dataset.userId = userId;
      
      // Get the contenteditable element
      const editableDiv = this.$refs.messenger_input;
      if (!editableDiv) return;
      
      // Clear all existing content
      editableDiv.innerHTML = '';
      
      // Create new paragraph
      const newParagraph = document.createElement('p');
      newParagraph.className = 'mb-0 message-input-line';
      
      // Add buffer elements around the tagged user
      const leftBufferElement = document.createElement('img');
      const rightBufferElement = document.createElement('img');

      // Make buffer elements invisible inline anchors (no broken-image rendering)
      [leftBufferElement, rightBufferElement].forEach(el => {
        el.style.cssText = 'width:0;height:0;border:0;margin:0;padding:0;display:inline;vertical-align:baseline;';
        el.setAttribute('aria-hidden', 'true');
      });
      
      // Append elements to paragraph: leftBuffer + contentObj + rightBuffer + space
      newParagraph.appendChild(leftBufferElement);
      newParagraph.appendChild(contentObj);
      newParagraph.appendChild(rightBufferElement);
      
      // Add a space after the tagged user for typing
      const spaceTextNode = document.createTextNode(' ');
      newParagraph.appendChild(spaceTextNode);
      
      // Add paragraph to the contenteditable div
      editableDiv.appendChild(newParagraph);
      
      // Set cursor position right after the space (ready for typing)
      const selection = window.getSelection();
      const range = document.createRange();
      
      // Position cursor at the end of the space text node
      range.setStart(spaceTextNode, 1); // After the space character
      range.setEnd(spaceTextNode, 1);
      range.collapse(true);
      
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Focus the contenteditable element
      editableDiv.focus();
      
      // Update tagged users
      this.setTaggedUsers();
    },
    filePondProcess(fieldName, file, metadata, load, error, progress, abort) {
      const self = this;
      const formData = new FormData();
      formData.append(fieldName, file);
      formData.append('ThreadId', this.threadId);
      formData.append('UserId', this.current_user_guid);

      let uploadPreviewSize = this.upload_preview_files.push({
        url: URL.createObjectURL(file),
        status: 'ERROR',
        type: file.type,
        name: file.name,
      });
      this.uploading_file = true;
      this.uploading_file_error = false;

      const { promise, abort: abortRequest } = this.mainStore.UploadFile(formData);

      promise.then(responseData => {
        load(responseData);
        self.upload_preview_files[uploadPreviewSize - 1].status = 'DONE';
        self.uploading_file = false;
      }).catch(err => {
        error(err || this.$t('messaging.input.uploadFailed'));
        self.upload_preview_files[uploadPreviewSize - 1].status = 'ERROR';
        self.uploading_file = false;
        self.uploading_file_error = true;
      });

      return {
        abort: () => {
          abortRequest();
          abort();
        },
      };
    },
    removeKeyboardSpacing() {},
    cancelMessage() {
      this.$emit('cancelMessage');
    },
    focusMessengerInput(e) {
      let eTargetParent = e.target.parentElement || null;
      if(this.message_send_ready && e.target === e.currentTarget || eTargetParent === e.currentTarget) {
        this.$refs.messenger_input.focus();
      }
    },
    /**
     * Puts text into the composer WITHOUT sending it, leaving the caret at the end so the person can
     * edit it or keep typing.
     *
     * Reached through the ref because this component's props are one-way and clear-only —
     * `clearMessengerInput` is a counter that wipes the box, and nothing seeds it. Callers that want to
     * offer a starting point rather than say something on the person's behalf (a morning-brief
     * finding's starter question, a voice transcription) come through here.
     *
     * Returns false when the contenteditable is not mounted, so a caller can tell "seeded" from
     * "nothing happened" instead of carrying on as though the text is in the box.
     */
    seedMessengerInput(text) {
      const inputEl = this.$refs.messenger_input;
      if (!inputEl) return false;

      // Clear existing content and add new text
      inputEl.textContent = text;
      this.new_message = text;

      // Ensure proper structure
      this.ensureParagraphTag();

      // Focus and place cursor at end
      inputEl.focus();
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(inputEl);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);

      return true;
    },
    resetThread() {
      const self = this;
      if(!this.message_send_ready || !this.isSystemAi)
        return;
      
      this.processing_thread_reset = true;
      this.mainStore.ThreadsReset({}).then(response => {
        console.log('ThreadsReset completed successfully');
        self.processing_thread_reset = false;
        self.$emit('resetCurrentThread');
      }, error => {
        setTimeout(() => {
          console.log('Error during ThreadsReset:', error);
          self.processing_thread_reset = false;
        }, 150);
      });
    },
    // ========== VOICE RECORDING METHODS ==========
    toggleRecording() {
      console.log('[Voice] toggleRecording called, isRecording:', this.isRecording, 'message_send_ready:', this.message_send_ready);
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    },
    async startRecording() {
      if (!this.message_send_ready || this.isRecording) return;
      
      // Barge-in: stop TTS playback (local + server) when STT starts
      this.bargeIn();
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Setup VAD (Voice Activity Detection)
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = this.audioContext.createMediaStreamSource(stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        source.connect(this.analyser);
        
        // Determine best mime type - prefer formats ffmpeg handles well
        let mimeType = 'audio/webm';
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        }
        console.log('[Voice] Recording with mimeType:', mimeType);
        
        this.mediaRecorder = new MediaRecorder(stream, { mimeType });
        this.audioChunks = [];
        
        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            this.audioChunks.push(e.data);
          }
        };
        
        this.mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(this.audioChunks, { type: mimeType });
          if (this.speechDetected) {
            await this.transcribeAudio(audioBlob);
          } else {
            console.log('[Voice] No speech detected, skipping transcription');
          }
          
          // Stop all tracks to release the microphone
          stream.getTracks().forEach(track => track.stop());
          
          // Cleanup audio context
          if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
          }
        };
        
        this.mediaRecorder.start(100); // Collect in 100ms chunks
        this.isRecording = true;
        this.speechDetected = false;
        this.voiceWarning = null;
        this.startVAD();

        // Safety net: max recording duration in case VAD never triggers stop
        this.maxRecordingTimeout = setTimeout(() => {
          if (this.isRecording) {
            console.log('[Voice] Max recording duration reached, stopping');
            this.stopRecording();
          }
        }, this.maxRecordingDuration);

      } catch (err) {
        console.error('Failed to start recording:', err);
        this.voiceWarning = this.$t('messaging.input.micDenied');
        this.$emit('voiceError', this.voiceWarning);
      }
    },
    
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
        
        // Clear timers
        if (this.silenceTimer) {
          clearTimeout(this.silenceTimer);
          this.silenceTimer = null;
        }
        if (this.maxRecordingTimeout) {
          clearTimeout(this.maxRecordingTimeout);
          this.maxRecordingTimeout = null;
        }
      }
    },

    startVAD() {
      const dataArray = new Float32Array(this.analyser.fftSize);
      
      const checkAudio = () => {
        if (!this.isRecording || !this.analyser) return;
        
        this.analyser.getFloatTimeDomainData(dataArray);
        const rms = Math.sqrt(dataArray.reduce((sum, v) => sum + v * v, 0) / dataArray.length);
        
        if (rms < this.silenceThreshold) {
          // Silence detected
          if (!this.silenceTimer) {
            this.silenceTimer = setTimeout(() => {
              if (this.isRecording) {
                this.stopRecording();
              }
            }, this.silenceDelay);
          }
        } else {
          // Sound detected, reset timer
          this.speechDetected = true;
          if (this.silenceTimer) {
            clearTimeout(this.silenceTimer);
            this.silenceTimer = null;
          }
        }
        
        requestAnimationFrame(checkAudio);
      };
      
      checkAudio();
    },
    
    async transcribeAudio(audioBlob) {
      if (!audioBlob || audioBlob.size < 1000) {
        // Audio too short, likely accidental click
        return;
      }
      
      this.isTranscribing = true;
      
      const formData = new FormData();
      // Use correct extension based on blob type
      const ext = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
      formData.append('audio', audioBlob, `recording.${ext}`);
      
      // Get user's language preference from Pinia store if available
      formData.append('language', this.transcription_language);
      
      try {
        const response = await fetch(`${this.messenger_api_base_uri}voice/transcribe`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.user_token}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Transcription failed: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.lowConfidence && result.suggestion) {
          this.voiceWarning = result.suggestion;
          this.$emit('voiceWarning', result.suggestion);
        }
        
        if (result.text && result.text.trim()) {
          // Inject transcribed text into the input
          if (this.seedMessengerInput(result.text)) {
            // Emit event for parent component
            this.$emit('voiceTranscribed', result.text);
            
            // Auto-send if enabled
            if (this.autoSendOnTranscribe && result.text.trim()) {
              await this.$nextTick();
              this.sendMessage(true);
            }
          }
        } else if (!result.lowConfidence) {
          this.voiceWarning = this.$t('messaging.input.audioNotUnderstood');
          this.$emit('voiceWarning', this.voiceWarning);
        }
        
      } catch (err) {
        console.error('Transcription error:', err);
        this.voiceWarning = this.$t('messaging.input.transcriptionFailed');
        this.$emit('voiceError', this.voiceWarning);
      } finally {
        this.isTranscribing = false;
      }
    },
    
    // ========== TTS PLAYBACK METHODS ==========
    // PCM format constants (must match server WAV header: 24kHz, 16-bit, mono)
    _ttsSampleRate() { return 24000; },
    _ttsWavHeaderSize() { return 44; },

    /**
     * Convert 16-bit signed PCM bytes to Float32 samples for Web Audio API
     */
    _pcmToFloat32(pcmBytes) {
      const validLength = pcmBytes.length & ~1; // Ensure even byte count
      const int16 = new Int16Array(pcmBytes.buffer, pcmBytes.byteOffset, validLength / 2);
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) {
        float32[i] = int16[i] / 32768;
      }
      return float32;
    },

    /**
     * Simple hash function for TTS cache keys
     */
    _hashText(text) {
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        const c = text.charCodeAt(i);
        hash = ((hash << 5) - hash) + c;
        hash = hash & hash;
      }
      return 't' + hash.toString(); // 't' prefix prevents numeric key coercion — JS objects sort integer-like keys numerically, breaking Object.keys() insertion order needed for FIFO cache eviction
    },

    async playTts(text, turnId = null) {
      if (!text || !text.trim()) return;
      
      // Stop any currently playing audio (barge-in)
      this.stopTts();
      
      this.ttsTurnId = turnId || `turn-${Date.now()}`;
      console.log('[TTS] Starting playback for turn:', this.ttsTurnId);
      
      try {
        this.isTtsPlaying = true;
        const sampleRate = this._ttsSampleRate();
        const cacheKey = this._hashText(text);

        // Check cache first — play immediately without network request
        const cachedSamples = this.ttsAudioCache[cacheKey];
        if (cachedSamples) {
          console.log('[TTS] Using cached audio');
          const ctx = new AudioContext({ sampleRate });
          this.ttsAudio = ctx;
          const audioBuffer = ctx.createBuffer(1, cachedSamples.length, sampleRate);
          audioBuffer.getChannelData(0).set(cachedSamples);
          const source = ctx.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(ctx.destination);
          source.onended = () => {
            console.log('[TTS] Playback complete');
            if (this.ttsAudio) {
              this.ttsAudio.close().catch(() => {});
              this.ttsAudio = null;
            }
            this.isTtsPlaying = false;
            this.ttsTurnId = null;
            this.ttsAbort = null;
            this.$emit('ttsEnded');
          };
          source.start();
          this.$emit('ttsStarted', this.ttsTurnId);
          return;
        }

        // Stream from server — audio starts as chunks arrive
        const abortController = new AbortController();
        this.ttsAbort = abortController;

        const response = await fetch(`${this.messenger_api_base_uri}voice/synthesize`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.user_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: text,
            turnId: this.ttsTurnId,
            stream: true
          }),
          signal: abortController.signal
        });
        
        if (!response.ok) {
          console.error('[TTS] Synthesis failed:', response.status);
          this.isTtsPlaying = false;
          return;
        }

        const ctx = new AudioContext({ sampleRate });
        this.ttsAudio = ctx;

        const reader = response.body.getReader();
        const wavHeaderSize = this._ttsWavHeaderSize();
        let scheduledTime = ctx.currentTime;
        let headerSkipped = false;
        let headerBuffer = new Uint8Array(0);
        let carryByte = null;  // Holds an odd trailing byte between chunks to maintain 16-bit alignment
        const allSamples = [];
        let lastSource = null;
        let ttsStartedFired = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          let pcmData;

          if (!headerSkipped) {
            // Accumulate bytes until we have the full WAV header to skip
            const merged = new Uint8Array(headerBuffer.length + value.length);
            merged.set(headerBuffer);
            merged.set(value, headerBuffer.length);
            headerBuffer = merged;

            if (headerBuffer.length < wavHeaderSize) continue;

            pcmData = headerBuffer.slice(wavHeaderSize);
            headerSkipped = true;
            headerBuffer = null;

            if (pcmData.length < 2) continue;
          } else {
            pcmData = value;
          }

          // Prepend any carry byte from the previous odd-length chunk to maintain
          // 16-bit sample alignment across HTTP/2 frame boundaries
          if (carryByte !== null) {
            const merged = new Uint8Array(1 + pcmData.length);
            merged[0] = carryByte;
            merged.set(pcmData, 1);
            pcmData = merged;
            carryByte = null;
          }

          // If this chunk ends on an odd byte, save it for the next iteration
          if (pcmData.length & 1) {
            carryByte = pcmData[pcmData.length - 1];
            pcmData = pcmData.slice(0, pcmData.length - 1);
          }

          const samples = this._pcmToFloat32(pcmData);
          if (samples.length === 0) continue;

          allSamples.push(samples);

          // Create an AudioBuffer and schedule it for gapless playback
          const audioBuffer = ctx.createBuffer(1, samples.length, sampleRate);
          audioBuffer.getChannelData(0).set(samples);

          const source = ctx.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(ctx.destination);

          const startTime = Math.max(scheduledTime, ctx.currentTime);
          source.start(startTime);
          scheduledTime = startTime + audioBuffer.duration;
          lastSource = source;

          if (!ttsStartedFired) {
            ttsStartedFired = true;
            this.$emit('ttsStarted', this.ttsTurnId);
          }
        }

        // Cache the complete audio for future replays
        if (allSamples.length > 0) {
          const totalLength = allSamples.reduce((sum, s) => sum + s.length, 0);
          const cached = new Float32Array(totalLength);
          let offset = 0;
          for (const s of allSamples) {
            cached.set(s, offset);
            offset += s.length;
          }
          // Evict oldest entry if at limit
          const cacheKeys = Object.keys(this.ttsAudioCache);
          if (cacheKeys.length >= 10) delete this.ttsAudioCache[cacheKeys[0]];
          this.ttsAudioCache[cacheKey] = cached;
        }

        // Fire ttsEnded when the last scheduled buffer finishes
        if (lastSource) {
          lastSource.onended = () => {
            console.log('[TTS] Playback complete');
            if (this.ttsAudio) {
              this.ttsAudio.close().catch(() => {});
              this.ttsAudio = null;
            }
            this.isTtsPlaying = false;
            this.ttsTurnId = null;
            this.ttsAbort = null;
            this.$emit('ttsEnded');
          };
        } else {
          if (this.ttsAudio) {
            this.ttsAudio.close().catch(() => {});
            this.ttsAudio = null;
          }
          this.isTtsPlaying = false;
          this.ttsTurnId = null;
          this.ttsAbort = null;
          this.$emit('ttsEnded');
        }

      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('[TTS] Streaming aborted (barge-in or stop)');
        } else {
          console.error('[TTS] Playback error:', err);
        }
        this.isTtsPlaying = false;
        this.ttsTurnId = null;
        this.ttsAbort = null;
      }
    },
    
    stopTts() {
      // Abort any in-flight streaming fetch
      if (this.ttsAbort) {
        this.ttsAbort.abort();
        this.ttsAbort = null;
      }

      // Close the AudioContext to stop all scheduled buffers
      if (this.ttsAudio) {
        console.log('[TTS] Stopping playback');
        this.ttsAudio.close().catch(() => {});
        this.ttsAudio = null;
      }

      // Cancel server-side streaming if we have a turn ID
      if (this.ttsTurnId) {
        fetch(`${this.messenger_api_base_uri}voice/cancel`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.user_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            turnId: this.ttsTurnId
          })
        })
        .then(() => console.log('[TTS] Cancel sent to server'))
        .catch(err => console.error('[TTS] Cancel request failed:', err));
      }

      this.ttsTurnId = null;
      this.isTtsPlaying = false;
    },

    bargeIn() {
      console.log('[TTS] Barge-in triggered');
      this.stopTts();
      this.$emit('voiceBargeIn');
    },
    
    toggleTts() {
      if (this.isTtsPlaying) {
        this.stopTts();
      }
    }
    // ========== END TTS PLAYBACK METHODS ==========
    // ========== END VOICE RECORDING METHODS ==========
  },
  watch: {
    // The mounted() fetch only covers a composer that STARTS on the AI thread. This component is
    // not re-created on thread switch (constant key), so when the user opens a human thread first
    // and then switches to Rey, isSystemAi flips here instead — without this watcher the Groups
    // section never loads for the rest of the session (PR #976 review). The store action caches
    // and soft-fails, so repeat flips are free.
    isSystemAi(newValue) {
      if (newValue)
        this.fetchDealerGroups();
    },
    show_tagged_users(newValue) {
      if(!newValue)
        this.active_tagged_user = null;
      else {
        this.setTaggedUsers();
        this.active_tagged_user = this.active_tagged_user || 0;
        this.scrollActiveTaggedUser();
      }
    },
    user_tag_list(newValue) {
      if(this.$FUSarrayHasEntries(newValue))
        this.active_tagged_user = 0;
    },
    messageUser(newValue) {
      if(newValue && this.ent_users_by_guid[newValue])
        this.replyToMessage(this.ent_users_by_guid[newValue]);
    },
    clearMessengerInput(newValue) {
      if(newValue > 0)
        this.$refs.messenger_input.innerHTML = '';
    },
    // A multi-store user switching stores is asking a different question: the add-on is bought per
    // rooftop, so the previous store's answer must not be carried over.
    current_cif() {
      this.refreshAttachmentAvailability();
    },
  }
}
</script>